import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getJson, patchJson, postForm, postJson } from "../api";
import CameraCapture from "../components/CameraCapture";
import SignaturePad from "../components/SignaturePad";
import {
  BiometryResultCard,
  buildBiometryErrorMessage,
  buildRows,
  CaptureResultCard,
  ExpandableInfoCard,
  ExtractedDriverCard,
  FeedbackPanel,
  InfoCard,
  Input,
  LastBiometryCard,
  mergeDniRegistroData,
  OcrResultCard,
  PortalLayout,
  PortalListItem,
  ProcessingPanel,
  StatusBadge,
  StatusMessage,
  formatDate,
  humanizeLabel,
  ChoferSessionSummary,
} from "./shared";

const choferHeroLayout = {
  heroClassName: "transport-app-header",
  heroContentClassName: "transport-app-header-left",
  titleClassName: "transport-app-header-title",
  heroAsideClassName: "transport-app-user-box",
};

function resolveDetectedEntityType(analysis) {
  const suggested = String(analysis?.entidad_sugerida || "").trim().toLowerCase();

  if (suggested === "acoplado" || suggested === "unidad") {
    return suggested;
  }

  const text = `${String(analysis?.datos_extraidos?.tipo || "")}`.toUpperCase();
  if (/(ACOPLAD|REMOLQUE|SEMI\s*REMOLQUE|SEMIRREMOLQUE)/.test(text)) {
    return "acoplado";
  }

  return "unidad";
}

function getUnitDocumentLabel(documentType) {
  const labels = {
    rto: "RTO",
    seguro_camion: "Seguro de camión",
    seguro_acoplado: "Seguro de remolque",
    cedula_frente: "Cédula frente",
    cedula_dorso: "Cédula dorso",
  };
  return labels[documentType] || humanizeLabel(documentType || "documento");
}

function getOperationDocumentRequestLabel(request) {
  return request?.metadata?.document_label || humanizeLabel(request?.document_type || "documento");
}

function hasRequiredIdentityFields(identity = {}) {
  return Boolean(identity.cuit && identity.nombre && identity.apellido);
}

function describeMissingIdentityFields(identity = {}) {
  const missing = [];
  if (!identity.cuit) {
    missing.push("CUIT");
  }
  if (!identity.nombre) {
    missing.push("nombre");
  }
  if (!identity.apellido) {
    missing.push("apellido");
  }
  if (!missing.length) {
    return "los datos requeridos";
  }
  return missing.join(", ");
}

function getChoferPortalMode() {
  const mode = String(readHashQuery().get("mode") || "").trim().toLowerCase();
  if (["login", "recover", "register"].includes(mode)) {
    return mode;
  }
  return "access";
}

function navigateChoferPortal(mode = "access", forceHash = false) {
  const useHash = forceHash || (mode && mode !== "access");
  const nextHash = useHash ? `#/portal-chofer?mode=${mode}` : "#/portal-chofer";
  if (window.location.hash === nextHash) {
    return;
  }
  window.location.hash = nextHash;
}

const CHOFER_LOGIN_METHODS_ENABLED = process.env.REACT_APP_CHOFER_LOGIN_METHODS !== "false";

const DEFAULT_CHOFER_DOCUMENT_DEFINITIONS = [
  { tipo: "dni", title: "DNI", virtual: true },
  { tipo: "licencia", title: "Licencia", entidadTipo: "chofer" },
  { tipo: "psicofisico", title: "Psicofisico", entidadTipo: "persona" },
  { tipo: "seguro_vida", title: "Seguro de vida", entidadTipo: "persona" },
  { tipo: "f931", title: "F931", entidadTipo: "persona" },
  { tipo: "biometria", title: "Biometría", virtual: true },
];

function getDriverDocumentDisplayPriority(documentType) {
  const normalized = String(documentType || "").toLowerCase();
  if (normalized === "dni" || normalized.startsWith("dni")) {
    return 0;
  }
  if (normalized === "biometria" || normalized.includes("biometr") || normalized.includes("selfie")) {
    return 1;
  }
  return 100;
}

function buildChoferDocumentDefinitions(policyCatalog = []) {
  const driverEntries = (policyCatalog || []).filter((entry) => entry.entity_type === "driver");
  if (!driverEntries.length) {
    return DEFAULT_CHOFER_DOCUMENT_DEFINITIONS;
  }

  const definitionMap = new Map();
  driverEntries.forEach((entry) => {
    if (["dni_frente", "dni_dorso"].includes(entry.document_type)) {
      definitionMap.set("dni", { tipo: "dni", title: "DNI", virtual: true, sort_order: entry.sort_order || 0 });
      return;
    }

    if (entry.document_type === "selfie_biometria") {
      definitionMap.set("biometria", { tipo: "biometria", title: "Biometría", virtual: true, sort_order: entry.sort_order || 0 });
      return;
    }

    definitionMap.set(entry.document_type, {
      tipo: entry.document_type,
      title: entry.label || humanizeLabel(entry.document_type),
      entidadTipo: "chofer",
      sort_order: entry.sort_order || 100,
    });
  });

  const definitions = Array.from(definitionMap.values()).sort((a, b) => {
    const aPriority = getDriverDocumentDisplayPriority(a.tipo);
    const bPriority = getDriverDocumentDisplayPriority(b.tipo);
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    if ((a.sort_order || 100) !== (b.sort_order || 100)) {
      return (a.sort_order || 100) - (b.sort_order || 100);
    }
    return a.title.localeCompare(b.title);
  });

  return definitions.length ? definitions : DEFAULT_CHOFER_DOCUMENT_DEFINITIONS;
}

export function PortalChoferScreen() {
  const [mode, setMode] = useState(() => getChoferPortalMode());

  useEffect(() => {
    const syncMode = () => setMode(getChoferPortalMode());
    window.addEventListener("hashchange", syncMode);
    return () => window.removeEventListener("hashchange", syncMode);
  }, []);

  if (mode === "register") {
    return <PortalChoferIdentityFlow flowMode="register" />;
  }

  if (mode === "recover") {
    return <PortalChoferIdentityFlow flowMode="recover" />;
  }

  return <PortalChoferAccessScreen initialView={mode === "login" ? "login" : ""} />;
}

function PortalChoferAccessScreen({ initialView = "" }) {
  const [activeView, setActiveView] = useState(initialView);
  const [loginCuit, setLoginCuit] = useState(() => window.localStorage.getItem("portalChoferCuit") || "");
  const [loginCode, setLoginCode] = useState("");
  const [loginProcessing, setLoginProcessing] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginStage, setLoginStage] = useState("idle");
  const [loginHint, setLoginHint] = useState("");
  const [loginMockCode, setLoginMockCode] = useState("");

  useEffect(() => {
    setActiveView(initialView);
  }, [initialView]);

  async function requestLoginCode() {
    const normalizedCuit = String(loginCuit || "").replace(/\D/g, "");

    if (normalizedCuit.length !== 11) {
      setLoginError("Ingresá el CUIT del chofer para continuar.");
      return;
    }

    setLoginProcessing(true);
    setLoginError("");
    try {
      const response = await postJson(`/portal-chofer/choferes/${normalizedCuit}/login-whatsapp/request`, {});
      setLoginStage("pending");
      setLoginHint(response.data?.verification_hint || "Te enviamos un código por WhatsApp.");
      setLoginMockCode(response.data?.mock_verification_code || "");
      setLoginCode("");
    } catch (error) {
      setLoginError(error?.message || "No pudimos iniciar el ingreso del chofer.");
    } finally {
      setLoginProcessing(false);
    }
  }

  async function submitLogin(event) {
    event?.preventDefault();
    const normalizedCuit = String(loginCuit || "").replace(/\D/g, "");

    if (loginStage !== "pending") {
      await requestLoginCode();
      return;
    }

    if (normalizedCuit.length !== 11) {
      setLoginError("Ingresá el CUIT del chofer para continuar.");
      return;
    }
    if (!loginCode) {
      setLoginError("Ingresá el código que enviamos por WhatsApp.");
      return;
    }

    setLoginProcessing(true);
    setLoginError("");
    try {
      await postJson(`/portal-chofer/choferes/${normalizedCuit}/login-whatsapp/verify`, {
        code: loginCode,
      });
      window.localStorage.setItem("portalChoferCuit", normalizedCuit);
      redirectToChoferApp(normalizedCuit);
    } catch (error) {
      setLoginError(error?.message || "No pudimos validar el código de WhatsApp.");
    } finally {
      setLoginProcessing(false);
    }
  }

  return (
    <PortalLayout
      {...choferHeroLayout}
      eyebrow="Portal Chofer"
      title={
        <>
          <span>Acceso</span>
          <span>del chofer</span>
        </>
      }
      description="Si el chofer ya validó WhatsApp, ingresá con código. Si todavía no lo validó, completá el alta con DNI y biometría."
      backHref={getChoferAppHomeHash()}
    >
      <section className="portal-section-grid">
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>¿Qué necesitás hacer?</h2>
          </div>
            <div className="login-method-grid">
              {[
                {
                  value: "register",
                  title: "Crear cuenta",
                  description: "Si el chofer todavía no validó WhatsApp, completá el alta con DNI, biometría y número de WhatsApp.",
                  buttonLabel: "Crear cuenta",
                  onClick: () => navigateChoferPortal("register"),
                },
                {
                  value: "login",
                  title: "Ingresar",
                  description: "Ingresá con el código que enviamos al WhatsApp validado del chofer.",
                  buttonLabel: "Ir a login",
                  onClick: () => {
                    setActiveView("login");
                    navigateChoferPortal("login");
                  },
                },
              ].map((item) => (
                <div key={item.value} className={`login-method-card ${activeView === item.value ? "active" : ""}`}>
                  <div className="login-method-card-top">
                    <div>
                      <strong>{item.title}</strong>
                      <p className="login-method-card-desc">{item.description}</p>
                    </div>
                  </div>
                  <button className="submit-button" type="button" onClick={item.onClick}>
                    {item.buttonLabel}
                  </button>
                </div>
              ))}
            </div>
        </section>
      </section>

      {activeView === "login" ? (
        <section className="portal-section-grid">
          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>WhatsApp</h2>
            </div>
            <div className="panel-body">
              <div className="portal-list">
                <PortalListItem
                  title={loginStage === "pending" ? "Confirmá el código" : "Ingresá con WhatsApp"}
                  description={
                    loginStage === "pending"
                      ? "Usá el código que enviamos al WhatsApp validado del chofer."
                      : "El ingreso del chofer se resuelve con el CUIT y un código enviado al WhatsApp previamente validado."
                  }
                />
              </div>
              <form onSubmit={(event) => submitLogin(event)} className="login-method-form">
                <Input label="CUIT del chofer" value={loginCuit} onChange={setLoginCuit} autoComplete="off" />
                {loginStage === "pending" ? (
                  <Input
                    label="Código de WhatsApp"
                    value={loginCode}
                    onChange={setLoginCode}
                    autoComplete="one-time-code"
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        submitLogin(event);
                      }
                    }}
                  />
                ) : null}
              </form>
              {loginHint ? <StatusMessage message={loginHint} /> : null}
              {loginMockCode ? <StatusMessage message={`Código mock: ${loginMockCode}`} /> : null}
              {loginError ? <StatusMessage message={loginError} /> : null}
              <div className="biometry-login-actions">
                {loginStage === "idle" ? (
                  <button className="submit-button" type="button" onClick={requestLoginCode} disabled={loginProcessing}>
                    <span className="hero-action-button-content">
                      <span className="whatsapp-button-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                          <path d="M19.05 4.95A9.9 9.9 0 0 0 12.02 2a10 10 0 0 0-8.67 15l-1.29 4.7 4.82-1.26A10 10 0 1 0 19.05 4.95Zm-7.03 15.4a8.3 8.3 0 0 1-4.22-1.15l-.3-.18-2.86.75.77-2.8-.2-.3a8.33 8.33 0 1 1 6.8 3.68Zm4.57-6.14c-.25-.13-1.47-.72-1.7-.8-.23-.08-.4-.13-.57.12-.17.25-.65.8-.8.96-.15.17-.3.19-.56.06-.25-.13-1.07-.39-2.04-1.24-.75-.66-1.25-1.47-1.4-1.72-.15-.25-.02-.38.11-.5.12-.12.25-.3.38-.44.13-.15.17-.25.25-.42.08-.17.04-.32-.02-.44-.06-.13-.57-1.38-.78-1.89-.2-.49-.4-.42-.57-.43h-.49c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.43 1.03 2.6.13.17 1.76 2.7 4.27 3.78.6.26 1.07.42 1.43.54.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.17-.48-.3Z" />
                        </svg>
                      </span>
                      <span>{loginProcessing ? "Enviando..." : "Enviar código por WhatsApp"}</span>
                    </span>
                  </button>
                ) : null}
                {loginStage === "pending" ? (
                  <button className="submit-button" type="button" onClick={submitLogin} disabled={loginProcessing}>
                    {loginProcessing ? "Verificando..." : "Ingresar con código"}
                  </button>
                ) : null}
                {loginStage === "pending" ? (
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => {
                      setLoginStage("idle");
                      setLoginCode("");
                      setLoginHint("");
                      setLoginMockCode("");
                      setLoginError("");
                    }}
                    disabled={loginProcessing}
                  >
                    Cambiar CUIT
                  </button>
                ) : null}
                {loginStage === "pending" ? (
                  <button className="secondary-button" type="button" onClick={requestLoginCode} disabled={loginProcessing}>
                    Reenviar código
                  </button>
                ) : null}
                {loginStage === "idle" ? (
                  <button className="secondary-button" type="button" onClick={() => navigateChoferPortal("recover")}>
                    Recuperar con DNI y biometría
                  </button>
                ) : null}
              </div>
              {loginStage === "pending" ? (
                <p className="biometry-login-note">
                  Usá el código enviado al WhatsApp validado del chofer para entrar al portal.
                </p>
              ) : null}
            </div>
          </section>
        </section>
      ) : null}
    </PortalLayout>
  );
}

function PortalChoferIdentityFlow({ flowMode = "register" }) {
  const isRecoveryFlow = flowMode === "recover";
  const [step, setStep] = useState(1);
  const heroIdentityTitle = (
    <>
      <span>{isRecoveryFlow ? "Recuperación" : "Verificación de"}</span>
      <span>{isRecoveryFlow ? "de cuenta" : "identidad"}</span>
    </>
  );
  const [dniFrenteFile, setDniFrenteFile] = useState(null);
  const [dniDorsoFile, setDniDorsoFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [identityData, setIdentityData] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [flowMessage, setFlowMessage] = useState("");
  const [loadingKey, setLoadingKey] = useState("");
  const [recoveryProfile, setRecoveryProfile] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState("");
  const [signupWhatsappPhone, setSignupWhatsappPhone] = useState("");
  const [signupWhatsappCode, setSignupWhatsappCode] = useState("");
  const [signupWhatsappStage, setSignupWhatsappStage] = useState("idle");
  const [signupWhatsappProcessing, setSignupWhatsappProcessing] = useState(false);
  const [signupWhatsappMessage, setSignupWhatsappMessage] = useState("");
  const [signupWhatsappMockCode, setSignupWhatsappMockCode] = useState("");
  const [signupWhatsappVerified, setSignupWhatsappVerified] = useState(false);
  const [ocrError, setOcrError] = useState("");
  const [retryStep, setRetryStep] = useState(1);
  const verificationRequestRef = useRef({
    inFlightKey: "",
    completedKey: "",
  });
  const loginPanelRef = useRef(null);
  const extractedIdentity = identityData?.datos_extraidos || {};
  const identityCuit = extractedIdentity.cuit || "";
  const heroIdentityName = `${extractedIdentity.nombre || ""} ${extractedIdentity.apellido || ""}`.trim() || "Chofer";
  const debugIdentity = process.env.REACT_APP_DEBUG_IDENTIDAD === "true";
  const resolvedChoferCuit = verificationResult?.chofer?.cuit || verificationResult?.persona?.cuit || identityCuit;
  const loginMethodFeatureEnabled = CHOFER_LOGIN_METHODS_ENABLED;
  const photoFlowItems = [
    {
      key: "frente",
      title: "DNI frente",
      tone: identityData?.frente?.is_dni_argentina ? "vigente" : loadingKey === "ocr-frente" ? "faltante" : "vencido",
      label: identityData?.frente?.is_dni_argentina ? "Listo" : loadingKey === "ocr-frente" ? "Procesando" : "Pendiente",
    },
    {
      key: "dorso",
      title: "DNI dorso",
      tone: identityData?.dorso?.is_dni_argentina ? "vigente" : loadingKey === "ocr-dorso" ? "faltante" : "vencido",
      label: identityData?.dorso?.is_dni_argentina ? "Listo" : loadingKey === "ocr-dorso" ? "Procesando" : "Pendiente",
    },
    {
      key: "selfie",
      title: "Selfie",
      tone:
        verificationResult?.biometria?.validado
          ? "vigente"
          : loadingKey === "identidad-verificar" || step === 5
            ? "faltante"
            : selfieFile
              ? "faltante"
              : "vencido",
      label:
        verificationResult?.biometria?.validado
          ? "Validada"
          : loadingKey === "identidad-verificar" || step === 5
            ? "Procesando"
            : selfieFile
              ? "Tomada"
              : "Pendiente",
    },
  ];

  useEffect(() => {
    let cancelled = false;
    const requestKey = [
      dniFrenteFile?.name || "",
      dniFrenteFile?.size || 0,
      dniDorsoFile?.name || "",
      dniDorsoFile?.size || 0,
      selfieFile?.name || "",
      selfieFile?.size || 0,
      identityCuit,
    ].join(":");

    async function verifyIdentity() {
      if (step !== 5 || !dniFrenteFile || !dniDorsoFile || !selfieFile) {
        return;
      }

      if (
        verificationRequestRef.current.inFlightKey === requestKey ||
        verificationRequestRef.current.completedKey === requestKey
      ) {
        return;
      }

      verificationRequestRef.current.inFlightKey = requestKey;

      setLoadingKey("identidad-verificar");
      setFlowMessage("Validando identidad y biometria...");
      debugLog(debugIdentity, "[portal-chofer] biometria start", { cuit: identityCuit });

      try {
        const response = await postForm(
          "/identidad/verificar",
          {
            cuit: identityCuit,
            nombre: extractedIdentity.nombre || "",
            apellido: extractedIdentity.apellido || "",
            nro_dni: extractedIdentity.nro_dni || "",
            fecha_nacimiento: extractedIdentity.fecha_nacimiento || "",
          },
          {
            dni_frente: dniFrenteFile,
            dni_dorso: dniDorsoFile,
            selfie: selfieFile,
          },
          {
            timeoutMs: 120000,
            timeoutMessage: "La validacion esta tardando mas de lo esperado. Espera un poco mas o reintenta la selfie si no termina.",
          }
        );

        if (!cancelled) {
          debugLog(debugIdentity, "[portal-chofer] biometria success", {
            cuit: response.data.persona?.cuit || response.data.chofer?.cuit || "",
            validado: response.data.biometria?.validado,
          });
          verificationRequestRef.current.completedKey = requestKey;
          setVerificationResult(response.data);
          window.localStorage.setItem("portalChoferCuit", response.data.persona?.cuit || response.data.chofer?.cuit || "");
          setFlowMessage(
            response.data.biometria.validado
              ? isRecoveryFlow
                ? "Identidad verificada. Ahora podés recuperar el acceso."
                : "Identidad verificada. Cuenta creada correctamente."
              : "La identidad no pudo validarse biometricamente."
          );
          setStep(6);
        }
      } catch (error) {
        if (!cancelled) {
          debugLog(debugIdentity, "[portal-chofer] biometria error", error);
          const message = buildBiometryErrorMessage(error.message, identityCuit);
          setVerificationResult({ error: message });
          setFlowMessage(message);
          setStep(6);
        }
      } finally {
        if (verificationRequestRef.current.inFlightKey === requestKey) {
          verificationRequestRef.current.inFlightKey = "";
        }
        if (!cancelled) {
          setLoadingKey("");
        }
      }
    }

    verifyIdentity();

    return () => {
      cancelled = true;
    };
  }, [
    step,
    dniFrenteFile,
    dniDorsoFile,
    selfieFile,
    identityCuit,
    extractedIdentity.nombre,
    extractedIdentity.apellido,
    extractedIdentity.nro_dni,
    extractedIdentity.fecha_nacimiento,
    debugIdentity,
    isRecoveryFlow,
  ]);

  useEffect(() => {
    if (step !== 5) {
      verificationRequestRef.current.inFlightKey = "";
    }
  }, [step]);

  useEffect(() => {
    if (!verificationResult?.biometria?.validado) {
      setSignupWhatsappVerified(false);
      setSignupWhatsappStage("idle");
      setSignupWhatsappCode("");
      setSignupWhatsappMessage("");
      setSignupWhatsappMockCode("");
      setRecoveryProfile(null);
      setRecoveryError("");
    }
  }, [verificationResult?.biometria?.validado, isRecoveryFlow]);

  useEffect(() => {
    if (!verificationResult?.biometria?.validado || (!isRecoveryFlow && signupWhatsappVerified)) {
      return;
    }

    loginPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [verificationResult?.biometria?.validado, isRecoveryFlow, signupWhatsappVerified]);

  useEffect(() => {
    if (!signupWhatsappVerified) {
      return;
    }
    const cuit = verificationResult?.chofer?.cuit || verificationResult?.persona?.cuit || identityCuit;
    if (!cuit) {
      return;
    }
    redirectToChoferApp(cuit);
  }, [signupWhatsappVerified, verificationResult?.chofer?.cuit, verificationResult?.persona?.cuit, identityCuit]);

  useEffect(() => {
    let cancelled = false;

    async function loadRecoveryProfile() {
      if (!isRecoveryFlow || !verificationResult?.biometria?.validado || !resolvedChoferCuit) {
        return;
      }

      setRecoveryLoading(true);
      setRecoveryError("");
      try {
        const response = await getJson(`/identidad/personas/${resolvedChoferCuit}`);
        if (!cancelled) {
          setRecoveryProfile(response.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          setRecoveryProfile(null);
          setRecoveryError(error?.message || "No pudimos cargar el método de acceso del chofer.");
        }
      } finally {
        if (!cancelled) {
          setRecoveryLoading(false);
        }
      }
    }

    loadRecoveryProfile();
    return () => {
      cancelled = true;
    };
  }, [isRecoveryFlow, verificationResult?.biometria?.validado, resolvedChoferCuit]);

  useEffect(() => {
    if (!isRecoveryFlow || !verificationResult?.biometria?.validado || !resolvedChoferCuit) {
      return;
    }
    window.localStorage.setItem("portalChoferCuit", resolvedChoferCuit);
    const timer = window.setTimeout(() => {
      redirectToChoferApp(resolvedChoferCuit);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isRecoveryFlow, verificationResult?.biometria?.validado, resolvedChoferCuit]);

  useEffect(() => {
    if (!verificationResult?.biometria?.validado || isRecoveryFlow) {
      return;
    }
    if (!signupWhatsappPhone) {
      const existingPhone =
        verificationResult?.chofer?.telefono ||
        recoveryProfile?.metodos_acceso?.find((method) => method?.tipo === "whatsapp" && !method.expired)?.metadata?.phone_number ||
        "";
      if (existingPhone) {
        setSignupWhatsappPhone(existingPhone);
      }
    }
  }, [verificationResult, isRecoveryFlow, signupWhatsappPhone, recoveryProfile]);

  async function processDniCapture(lado, file) {
    const nextLoadingKey = lado === "frente" ? "ocr-frente" : "ocr-dorso";
    setLoadingKey(nextLoadingKey);
    setOcrError("");
    setFlowMessage(`Procesando DNI ${lado}...`);
    setRetryStep(lado === "frente" ? 1 : 2);
    debugLog(debugIdentity, "[portal-chofer] OCR single start", {
      lado,
      file: file?.name || "",
    });

    try {
      const response = await postForm("/identidad/registro-dni", { lado }, { archivo: file }, {
        timeoutMs: 30000,
        timeoutMessage: "No pudimos leer tu foto. Reintenta.",
      });

      if (!response.data?.is_dni_argentina) {
        throw new Error("No pudimos leer tu foto. Reintenta.");
      }

      if (lado === "frente") {
        setDniFrenteFile(file);
        setIdentityData((current) => {
          const next = {
            frente: response.data,
            dorso: current?.dorso || null,
          };
          return {
            ...next,
            datos_extraidos: mergeDniRegistroData(next.frente, next.dorso),
          };
        });
        setFlowMessage("Frente procesado. Revisamos los datos y seguimos con el dorso.");
        setStep(2);
        return;
      }

      const currentData = identityData || {};
      const next = {
        frente: currentData?.frente || null,
        dorso: response.data,
      };
      const merged = mergeDniRegistroData(next.frente, next.dorso);
      const identityComplete = hasRequiredIdentityFields(merged);

      setDniDorsoFile(file);
      if (!identityComplete) {
        const missingDescription = describeMissingIdentityFields(merged);
        setFlowMessage(
          `No pudimos detectar ${missingDescription} con claridad. Repetí la captura del DNI frente con mejor foco antes de continuar.`
        );
        setIdentityData(null);
        setDniFrenteFile(null);
        setDniDorsoFile(null);
        setStep(1);
        return;
      }

      setIdentityData({
        ...next,
        datos_extraidos: merged,
      });
      setFlowMessage("Identidad detectada. Continua con la selfie.");
      setStep(4);
    } catch (error) {
      debugLog(debugIdentity, "[portal-chofer] OCR single error", error);
      const message = buildOcrErrorMessage(error.message);
      setOcrError(message);
      setFlowMessage(message);
      setStep(7);
    } finally {
      setLoadingKey("");
    }
  }

  const heroIdentityAside = <ChoferSessionSummary name={heroIdentityName} cuit={identityCuit} />;

  async function requestSignupWhatsappCode() {
    if (!verificationResult?.biometria?.validado) {
      setSignupWhatsappMessage("Necesitás validar la biometría antes de confirmar el WhatsApp.");
      return;
    }
    if (!signupWhatsappPhone) {
      setSignupWhatsappMessage("Ingresá el número de WhatsApp del chofer.");
      return;
    }
    const choferCuit = verificationResult?.chofer?.cuit || verificationResult?.persona?.cuit || identityCuit;
    if (!choferCuit) {
      setSignupWhatsappMessage("No pudimos resolver el CUIT del chofer para validar WhatsApp.");
      return;
    }
    setSignupWhatsappProcessing(true);
    setSignupWhatsappMessage("");
    try {
      const response = await postJson(`/portal-chofer/choferes/${choferCuit}/phone-verification/request`, {
        phone_number: signupWhatsappPhone,
      });
      setSignupWhatsappStage("pending");
      setSignupWhatsappCode("");
      setSignupWhatsappMockCode(response.data?.mock_verification_code || "");
      setSignupWhatsappMessage(response.data?.verification_hint || "Te enviamos un código por WhatsApp.");
      setFlowMessage("Biometría validada. Falta confirmar el código enviado al WhatsApp del chofer.");
    } catch (error) {
      setSignupWhatsappMessage(error?.message || "No pudimos enviar el código de WhatsApp.");
    } finally {
      setSignupWhatsappProcessing(false);
    }
  }

  async function confirmSignupWhatsappCode() {
    if (!signupWhatsappCode) {
      setSignupWhatsappMessage("Ingresá el código enviado por WhatsApp.");
      return;
    }
    const choferCuit = verificationResult?.chofer?.cuit || verificationResult?.persona?.cuit || identityCuit;
    if (!choferCuit) {
      setSignupWhatsappMessage("No pudimos resolver el CUIT del chofer para validar WhatsApp.");
      return;
    }
    setSignupWhatsappProcessing(true);
    setSignupWhatsappMessage("");
    try {
      await postJson(`/portal-chofer/choferes/${choferCuit}/phone-verification/verify`, {
        phone_number: signupWhatsappPhone,
        code: signupWhatsappCode,
      });
      setSignupWhatsappVerified(true);
      setSignupWhatsappStage("verified");
      setSignupWhatsappCode("");
      setSignupWhatsappMockCode("");
      setSignupWhatsappMessage("WhatsApp validado correctamente. Ya podés ingresar al portal.");
      setFlowMessage("WhatsApp validado correctamente. Ingresando al portal.");
    } catch (error) {
      setSignupWhatsappMessage(error?.message || "No pudimos verificar el código de WhatsApp.");
    } finally {
      setSignupWhatsappProcessing(false);
    }
  }

  return (
    <PortalLayout
      {...choferHeroLayout}
      eyebrow="Portal Chofer"
      title={heroIdentityTitle}
      description={isRecoveryFlow ? "Validá tu identidad con DNI y selfie para recuperar el acceso." : "Verifica tu identidad con DNI y selfie."}
      backHref={getChoferAppHomeHash(resolvedChoferCuit)}
      heroAside={heroIdentityAside}
    >
      <StatusMessage message={flowMessage} />

      <section className="detail-panel detail-panel-wide">
        <div className="capture-flow-grid">
          {photoFlowItems.map((item) => (
            <article className={`capture-flow-card capture-flow-card-${item.tone}`} key={item.key}>
              <strong>{item.title}</strong>
              <StatusBadge label={item.label} tone={item.tone} />
            </article>
          ))}
        </div>
      </section>

      {step === 1 ? (
        <section className="portal-section-grid">
          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Escanear DNI frente</h2>
            </div>
            <div className="panel-body">
              <CameraCapture
                mode="documento"
                title="DNI frente"
                description="Captura el frente del DNI con buena luz y foco. Al confirmar, lo procesamos automaticamente."
                onConfirm={(file) => processDniCapture("frente", file)}
              />
              {loadingKey === "ocr-frente" ? <ProcessingPanel title="Procesando frente del DNI" message="Estamos leyendo los datos del frente." /> : null}
            </div>
          </section>
        </section>
      ) : null}

      {step === 2 ? (
        <section className="portal-section-grid">
          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Escanear DNI dorso</h2>
            </div>
            <div className="panel-body">
              <CameraCapture
                mode="documento"
                title="DNI dorso"
                description="Captura el dorso del DNI. Al confirmar, completamos automaticamente la identidad."
                onConfirm={(file) => processDniCapture("dorso", file)}
              />
              {loadingKey === "ocr-dorso" ? <ProcessingPanel title="Procesando dorso del DNI" message="Estamos completando los datos de identidad." /> : null}
            </div>
          </section>
        </section>
      ) : null}

      {step === 3 ? (
        <section className="portal-section-grid">
          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>OCR de identidad</h2>
            </div>
            <div className="panel-body">
              <p>Estamos leyendo el DNI para resolver la identidad del chofer.</p>
              <ProcessingPanel title="Procesando identidad" message="No hace falta tocar nada. Cuando terminemos, seguimos automaticamente." />
            </div>
          </section>
        </section>
      ) : null}

      {step === 7 ? (
        <section className="portal-section-grid">
          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Error al procesar identidad</h2>
              <StatusBadge label="Reintentar" tone="faltante" />
            </div>
            <div className="panel-body">
              <FeedbackPanel message={ocrError || "No se pudo completar el OCR del DNI."} error />
              <div className="biometry-actions">
                <button
                  className="submit-button"
                  type="button"
                  onClick={() => {
                    setOcrError("");
                    setFlowMessage("");
                    setStep(retryStep);
                  }}
                >
                  Reintentar captura
                </button>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => {
                    setOcrError("");
                    setFlowMessage("");
                    if (retryStep === 2) {
                      setDniDorsoFile(null);
                      setIdentityData((current) => ({
                        frente: current?.frente || null,
                        dorso: null,
                        datos_extraidos: mergeDniRegistroData(current?.frente, null),
                      }));
                      setStep(2);
                      return;
                    }

                    setIdentityData(null);
                    setDniFrenteFile(null);
                    setDniDorsoFile(null);
                    setStep(1);
                  }}
                >
                  Volver a sacar foto
                </button>
              </div>
            </div>
          </section>
        </section>
      ) : null}

      {step === 4 ? (
        <section className="portal-section-grid">
          <section className="detail-panel">
            <div className="detail-panel-header">
              <h2>Tomar selfie</h2>
            </div>
            <div className="panel-body">
              <CameraCapture
                mode="selfie"
                title="Selfie"
                description="Captura una selfie centrada. Al confirmar, la biometria se validara automaticamente."
                onConfirm={(file) => {
                  setSelfieFile(file);
                  setStep(5);
                }}
              />
            </div>
          </section>
        </section>
      ) : null}

      {step === 5 ? (
        <section className="portal-section-grid">
          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Procesando biometria</h2>
            </div>
            <div className="panel-body">
              <ProcessingPanel title="Procesando biometria" message="Estamos validando la selfie contra el DNI. No hace falta tocar nada." />
            </div>
          </section>
        </section>
      ) : null}

      {step === 6 ? (
        <section className="portal-section-grid">
          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Resultado final</h2>
              {verificationResult?.biometria ? (
                <StatusBadge
                  label={verificationResult.biometria.validado ? "Identidad verificada" : "Identidad observada"}
                  tone={verificationResult.biometria.validado ? "vigente" : "vencido"}
                />
              ) : null}
            </div>
            <div className="panel-body">
              {loadingKey === "identidad-verificar" ? <StatusMessage message="Validando identidad y biometria..." /> : null}
              <div className="capture-flow-grid">
                <ExpandableCaptureFlowCard
                  title="DNI frente"
                  tone={identityData?.frente?.is_dni_argentina ? "vigente" : "vencido"}
                  label={identityData?.frente?.is_dni_argentina ? "Listo" : "Pendiente"}
                  file={dniFrenteFile}
                  rows={buildDniCaptureRows(identityData?.frente)}
                />
                <ExpandableCaptureFlowCard
                  title="DNI dorso"
                  tone={identityData?.dorso?.is_dni_argentina ? "vigente" : "vencido"}
                  label={identityData?.dorso?.is_dni_argentina ? "Listo" : "Pendiente"}
                  file={dniDorsoFile}
                  rows={buildDniCaptureRows(identityData?.dorso)}
                />
                <ExpandableCaptureFlowCard
                  title="Selfie"
                  tone={verificationResult?.biometria?.validado ? "vigente" : "vencido"}
                  label={verificationResult?.biometria?.validado ? "Validada" : "Observada"}
                  file={selfieFile}
                  rows={buildSelfieCaptureRows(verificationResult?.biometria)}
                />
              </div>
              {verificationResult?.persona ? (
                <ExpandableInfoCard
                  title="Identidad"
                  summary={verificationResult.persona.cuit || "Identidad detectada"}
                  badge={<StatusBadge label="Lista" tone="vigente" />}
                  rows={buildRows(verificationResult.persona, ["cuit", "nombre", "apellido", "nro_dni", "fecha_nacimiento"])}
                />
              ) : null}
              {verificationResult?.chofer ? (
                <ExpandableInfoCard
                  title="Chofer"
                  summary={verificationResult.chofer.cuit || "Chofer creado"}
                  badge={<StatusBadge label="Resuelto" tone="vigente" />}
                  rows={buildRows(verificationResult.chofer, ["cuit", "nombre", "apellido"])}
                />
              ) : null}
              {verificationResult?.biometria ? <BiometryResultCard result={verificationResult.biometria} /> : null}
              {verificationResult?.biometria?.validado && !isRecoveryFlow && loginMethodFeatureEnabled ? (
                <div className="biometry-login-panel" ref={loginPanelRef}>
                  <div className="biometry-login-note">
                    La cuenta del chofer queda habilitada cuando validás el número de WhatsApp. A partir de ahí el ingreso se hace con código por WhatsApp.
                  </div>
                  <Input
                    label="Teléfono WhatsApp"
                    value={signupWhatsappPhone}
                    onChange={setSignupWhatsappPhone}
                    autoComplete="tel"
                    placeholder="+5491123456789"
                  />
                  {signupWhatsappStage === "pending" ? (
                    <Input
                      label="Código de WhatsApp"
                      value={signupWhatsappCode}
                      onChange={setSignupWhatsappCode}
                      autoComplete="one-time-code"
                      placeholder="Ingresá el código de 6 dígitos"
                    />
                  ) : null}
                  <div className="biometry-login-actions">
                    <button
                      className="submit-button"
                      type="button"
                      disabled={signupWhatsappProcessing}
                      onClick={signupWhatsappStage === "pending" ? confirmSignupWhatsappCode : requestSignupWhatsappCode}
                    >
                      {signupWhatsappProcessing
                        ? (signupWhatsappStage === "pending" ? "Verificando..." : "Enviando...")
                        : (signupWhatsappStage === "pending" ? "Confirmar código" : "Enviar código por WhatsApp")}
                    </button>
                    {signupWhatsappStage === "pending" ? (
                      <button
                        className="secondary-button"
                        type="button"
                        disabled={signupWhatsappProcessing}
                        onClick={requestSignupWhatsappCode}
                      >
                        Reenviar código
                      </button>
                    ) : null}
                  </div>
                  {signupWhatsappMockCode ? <StatusMessage message={`Código mock: ${signupWhatsappMockCode}`} /> : null}
                  {signupWhatsappMessage ? <StatusMessage message={signupWhatsappMessage} /> : null}
                </div>
              ) : null}
              {verificationResult?.biometria?.validado && isRecoveryFlow ? (
                <div className="biometry-login-panel" ref={loginPanelRef}>
                  <div className="biometry-login-note">
                    Tu identidad quedó validada. La recuperación del chofer sigue con DNI y biometría, así que te vamos a redirigir al portal.
                  </div>
                  {recoveryLoading ? <ProcessingPanel title="Buscando cuenta" message="Estamos resolviendo el método de acceso asociado al chofer." /> : null}
                  {recoveryError ? <StatusMessage message={recoveryError} /> : null}
                  {!recoveryLoading && !recoveryError && !recoveryProfile?.chofer ? (
                    <>
                      <p className="biometry-login-note">No encontramos un chofer registrado para esta identidad. Tenés que crear la cuenta primero.</p>
                      <div className="biometry-login-actions">
                        <button className="submit-button" type="button" onClick={() => navigateChoferPortal("register")}>
                          Crear cuenta
                        </button>
                        <button className="secondary-button" type="button" onClick={() => navigateChoferPortal("login")}>
                          Volver al login
                        </button>
                      </div>
                    </>
                  ) : null}
                  {!recoveryLoading && recoveryProfile?.chofer ? (
                    <>
                      <ExpandableInfoCard
                        title="Recuperación"
                        summary="Identidad validada"
                        badge={
                          <StatusBadge
                            label="Resuelto"
                            tone="vigente"
                          />
                        }
                        rows={buildRows(
                          {
                            chofer: `${recoveryProfile.chofer?.nombre || ""} ${recoveryProfile.chofer?.apellido || ""}`.trim() || "-",
                            cuit: recoveryProfile.chofer?.cuit || resolvedChoferCuit || "-",
                            whatsapp_validado: recoveryProfile.metodos_acceso?.some((method) => method?.tipo === "whatsapp" && !method.expired) ? "Sí" : "No",
                          },
                          ["chofer", "cuit", "whatsapp_validado"]
                        )}
                      />
                      <p className="biometry-login-note">
                        Identidad y biometría confirmadas. Si no te redirigimos automáticamente, podés continuar al portal desde acá.
                      </p>
                      <div className="biometry-login-actions">
                        <button
                          className="submit-button"
                          type="button"
                          onClick={() => {
                            if (resolvedChoferCuit) {
                              window.localStorage.setItem("portalChoferCuit", resolvedChoferCuit);
                              redirectToChoferApp(resolvedChoferCuit);
                            }
                          }}
                        >
                          Ir al portal
                        </button>
                        <button className="secondary-button" type="button" onClick={() => navigateChoferPortal("login")}>
                          Volver al login
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}
              {verificationResult?.error ? <FeedbackPanel message={verificationResult.error} error /> : null}
              <div className="biometry-actions">
                {!verificationResult?.biometria?.validado ? (
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => {
                      setSelfieFile(null);
                      setVerificationResult(null);
                      setFlowMessage("");
                      setStep(4);
                    }}
                  >
                    Reintentar selfie
                  </button>
                ) : null}
              </div>
            </div>
          </section>
        </section>
      ) : null}
    </PortalLayout>
  );
}

function buildOcrErrorMessage(message) {
  if (!message) {
    return "No pudimos leer tu foto. Reintenta.";
  }

  return "No pudimos leer tu foto. Reintenta.";
}

function debugLog(enabled, message, payload) {
  if (!enabled) {
    return;
  }

  if (payload !== undefined) {
    console.info(message, payload);
    return;
  }

  console.info(message);
}

function readHashQuery() {
  const hash = window.location.hash || "";
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) {
    return new URLSearchParams();
  }
  return new URLSearchParams(hash.slice(queryIndex + 1));
}

function buildDniCaptureRows(data) {
  if (!data?.is_dni_argentina) {
    return [];
  }

  const extracted = data.datos_extraidos || {};
  const rows = [
    { label: "CUIT", value: extracted.cuit || "-" },
    { label: "Nombre", value: extracted.first_name || extracted.nombre || "-" },
    { label: "Apellido", value: extracted.last_name || extracted.apellido || "-" },
    { label: "Nombre completo", value: extracted.full_name || "-" },
    { label: "DNI", value: extracted.dni_number || extracted.nro_dni || "-" },
    { label: "Fecha de nacimiento", value: extracted.birth_date || extracted.fecha_nacimiento || "-" },
  ];

  if (extracted.sex) {
    rows.push({ label: "Sexo", value: extracted.sex });
  }

  if (extracted.tramite) {
    rows.push({ label: "Trámite", value: extracted.tramite });
  }

  if (extracted.issue_date) {
    rows.push({ label: "Expedición", value: extracted.issue_date });
  }

  if (extracted.expiry_date) {
    rows.push({ label: "Vencimiento", value: extracted.expiry_date });
  }

  return rows;
}

function buildSelfieCaptureRows(biometria) {
  if (!biometria) {
    return [];
  }

  return [
    { label: "Resultado", value: biometria.validado ? "Validada" : "Observada" },
    { label: "Similaridad", value: `${Number(biometria.similaridad).toFixed(2)}%` },
    { label: "Fecha de validacion", value: biometria.created_at ? formatDate(biometria.created_at) : "-" },
  ];
}

function ExpandableCaptureFlowCard({ title, tone, label, file, rows }) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(file);
    setPreviewUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [file]);

  return (
    <details className={`capture-flow-card capture-flow-card-${tone} capture-flow-card-expandable`}>
      <summary className="capture-flow-summary">
        <strong>{title}</strong>
        <StatusBadge label={label} tone={tone} />
      </summary>
      <div className="capture-flow-detail">
        {previewUrl ? (
          <div className="capture-flow-preview">
            <img src={previewUrl} alt={title} />
          </div>
        ) : null}
        {rows?.length ? (
          <div className="detail-table capture-flow-detail-table">
            {rows.map((row) => (
              <div className="detail-row" key={`${title}-${row.label}`}>
                <span>{row.label}</span>
                <strong>{row.value}</strong>
              </div>
            ))}
          </div>
        ) : (
          <p className="capture-flow-empty">Sin datos para mostrar.</p>
        )}
      </div>
    </details>
  );
}

function redirectToChoferApp(cuit) {
  const targetHash = cuit ? `#/portal-chofer/app/${cuit}` : "#/portal-chofer/app";
  const targetUrl = `${window.location.origin}${targetHash}`;
  if (window.location.href === targetUrl) {
    return;
  }
  window.location.replace(targetUrl);
}

function getChoferAppHomeHash(cuit = "") {
  const resolvedCuit = String(cuit || window.localStorage.getItem("portalChoferCuit") || "").trim();
  return resolvedCuit ? `#/portal-chofer/app/${resolvedCuit}` : "#/portal-chofer";
}

export function PortalChoferAppScreen({ cuitFromRoute = "" }) {
  const [cuit] = useState(cuitFromRoute || window.localStorage.getItem("portalChoferCuit") || "");
  const [documentPolicyCatalog, setDocumentPolicyCatalog] = useState([]);
  const [data, setData] = useState(null);
  const [, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openingDocumentId, setOpeningDocumentId] = useState("");
  const [documentPreviewUrls, setDocumentPreviewUrls] = useState({});
  const [savingLicencia, setSavingLicencia] = useState(false);
  const [activeDocumentCapture, setActiveDocumentCapture] = useState("");
  const [activeDocumentPreview, setActiveDocumentPreview] = useState("");
  const [pendingContracts, setPendingContracts] = useState([]);
  const [unitRegistrationRequests, setUnitRegistrationRequests] = useState([]);
  const [operationDocumentRequests, setOperationDocumentRequests] = useState([]);
  const visualDocuments = getLatestDocumentsByType([
    ...(data?.evidencias || []),
    ...(data?.chofer_documentos || []),
  ]);
  const documentsByType = Object.fromEntries(visualDocuments.map((documento) => [documento.tipo_documento, documento]));
  const chofer = data?.chofer || null;
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [whatsappCode, setWhatsappCode] = useState("");
  const [whatsappStage, setWhatsappStage] = useState("idle");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [whatsappMockCode, setWhatsappMockCode] = useState("");
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappResendVisible, setWhatsappResendVisible] = useState(false);
  const documentDefinitions = useMemo(
    () => buildChoferDocumentDefinitions(documentPolicyCatalog),
    [documentPolicyCatalog]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadDocumentPolicyCatalog() {
      try {
        const response = await getJson("/document-policies/catalog");
        if (!cancelled) {
          setDocumentPolicyCatalog(response.data || []);
        }
      } catch (_error) {
        if (!cancelled) {
          setDocumentPolicyCatalog([]);
        }
      }
    }

    loadDocumentPolicyCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!cuit) {
        setData(null);
        return;
      }

      setLoading(true);
      setMessage("");

      try {
        const response = await getJson(`/identidad/personas/${cuit}`);
        if (!cancelled) {
          setData(response.data);
          window.localStorage.setItem("portalChoferCuit", response.data.cuit);
        }
      } catch (error) {
        if (!cancelled) {
          setData(null);
          setMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [cuit]);

  useEffect(() => {
    let cancelled = false;

    async function loadPreviews() {
      const documentos = [
        ...(data?.evidencias || []),
        ...(data?.chofer_documentos || []),
      ];
      const imageDocuments = documentos.filter((documento) => documento?.mime_type?.startsWith("image/"));

      if (!imageDocuments.length) {
        setDocumentPreviewUrls({});
        return;
      }

      try {
        const results = await Promise.all(
          imageDocuments.map(async (documento) => {
            const response = await getJson(`/documentos/${documento.id}/url`);
            return [documento.id, response.data.url];
          })
        );

        if (!cancelled) {
          setDocumentPreviewUrls(Object.fromEntries(results));
        }
      } catch (_error) {
        if (!cancelled) {
          setDocumentPreviewUrls({});
        }
      }
    }

    loadPreviews();

    return () => {
      cancelled = true;
    };
  }, [data]);

  async function refreshIdentity() {
    if (!cuit) {
      return;
    }

    const response = await getJson(`/identidad/personas/${cuit}`);
    setData(response.data);
    window.localStorage.setItem("portalChoferCuit", response.data.cuit);
  }

  async function uploadDocumentFromCapture(tipoDocumento, file) {
    const definition = documentDefinitions.find((item) => item.tipo === tipoDocumento);

    if (!cuit || !definition || !file) {
      setMessage("No pudimos guardar la foto. Reintenta.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      await postForm(
        "/documentos",
        {
          entidad_tipo: definition.entidadTipo,
          entidad_id: cuit,
          tipo_documento: tipoDocumento,
        },
        { archivo: file }
      );
      await refreshIdentity();
      setActiveDocumentCapture("");
      setMessage("Documento cargado correctamente.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function openDocument(documentId) {
    setOpeningDocumentId(String(documentId));
    try {
      const response = await getJson(`/documentos/${documentId}/url`);
      window.open(response.data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setOpeningDocumentId("");
    }
  }

  async function processLicenciaAndSave(file) {
    if (!cuit || !file) {
      setMessage("Captura la licencia antes de procesarla.");
      return;
    }

    setSavingLicencia(true);
    setMessage("");

    try {
      const response = await postForm(`/identidad/personas/${cuit}/licencia`, {}, { archivo: file }, {
        timeoutMs: 60000,
        timeoutMessage: "El procesamiento de la licencia demoro demasiado. Reintenta la captura.",
      });
      await refreshIdentity();
      setActiveDocumentCapture("");
      setMessage(
        response.data.licencia?.missing_fields?.length
          ? "Licencia guardada, pero algunos datos quedaron en revision."
          : "Licencia procesada y guardada correctamente."
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSavingLicencia(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadRequestSummary() {
      if (!cuit) {
        setPendingContracts([]);
        setUnitRegistrationRequests([]);
        setOperationDocumentRequests([]);
        return;
      }

      try {
        const [pendingResponse, unitRequestResponse, operationDocumentResponse] = await Promise.allSettled([
          getJson(`/portal-chofer/choferes/${cuit}/contracts/pending`),
          getJson(`/portal-chofer/choferes/${cuit}/unit-registration-requests`),
          getJson(`/portal-chofer/choferes/${cuit}/operation-document-requests`),
        ]);
        if (!cancelled) {
          setPendingContracts(pendingResponse.status === "fulfilled" ? pendingResponse.value.data || [] : []);
          setUnitRegistrationRequests(unitRequestResponse.status === "fulfilled" ? unitRequestResponse.value.data || [] : []);
          setOperationDocumentRequests(
            operationDocumentResponse.status === "fulfilled" ? operationDocumentResponse.value.data || [] : []
          );
        }
      } catch (_error) {
        if (!cancelled) {
          setPendingContracts([]);
          setUnitRegistrationRequests([]);
          setOperationDocumentRequests([]);
        }
      }
    }

    loadRequestSummary();

    return () => {
      cancelled = true;
    };
  }, [cuit]);

  async function requestChoferWhatsappVerification() {
    if (!whatsappPhone) {
      setWhatsappMessage("Ingresá un teléfono de WhatsApp para enviar el código.");
      return;
    }

    setWhatsappLoading(true);
    setWhatsappMessage("");
    try {
      const response = await postJson(`/portal-chofer/choferes/${cuit}/phone-verification/request`, {
        phone_number: whatsappPhone,
      });
      setWhatsappStage("pending");
      setWhatsappResendVisible(false);
      setWhatsappMessage(response.data?.verification_hint || "Te enviamos un código por WhatsApp.");
      setWhatsappMockCode(response.data?.mock_verification_code || "");
    } catch (error) {
      setWhatsappMessage(error?.message || "No pudimos enviar el código de WhatsApp.");
    } finally {
      setWhatsappLoading(false);
    }
  }

  async function confirmChoferWhatsappVerification() {
    if (!whatsappCode) {
      setWhatsappMessage("Ingresá el código enviado por WhatsApp.");
      return;
    }

    setWhatsappLoading(true);
    setWhatsappMessage("");
    try {
      await postJson(`/portal-chofer/choferes/${cuit}/phone-verification/verify`, {
        phone_number: whatsappPhone,
        code: whatsappCode,
      });
      setWhatsappStage("verified");
      setWhatsappCode("");
      setWhatsappMockCode("");
      setWhatsappResendVisible(false);
      setWhatsappMessage("Teléfono validado exitosamente.");
      setWhatsappPanelVisible(false);
      await refreshIdentity();
    } catch (error) {
      setWhatsappResendVisible(true);
      setWhatsappMessage(error?.message || "No pudimos verificar el teléfono.");
    } finally {
      setWhatsappLoading(false);
    }
  }

  const [linkRequestsCount, setLinkRequestsCount] = useState(0);
  const [linkRequestsLoading, setLinkRequestsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadLinkRequests() {
      if (!cuit) {
        setLinkRequestsCount(0);
        return;
      }

      setLinkRequestsLoading(true);
      try {
        const response = await getJson(`/portal-chofer/choferes/${cuit}/transport-links`);
        if (!cancelled) {
          const pendingLinks = (response.data || []).filter((link) => link?.status === "pending");
          setLinkRequestsCount(pendingLinks.length);
        }
      } catch (_error) {
        if (!cancelled) {
          setLinkRequestsCount(0);
        }
      } finally {
        if (!cancelled) {
          setLinkRequestsLoading(false);
        }
      }
    }

    loadLinkRequests();
    return () => {
      cancelled = true;
    };
  }, [cuit]);

  const pendingUnitRegistrationCount = unitRegistrationRequests.filter((request) => request.status === "pending_acceptance").length;
  const pendingOperationDocumentCount = operationDocumentRequests.filter((request) => request.status === "pending").length;
  const pendingNotificationsCount =
    pendingOperationDocumentCount +
    pendingUnitRegistrationCount +
    pendingContracts.length +
    linkRequestsCount;
  const heroAppName = `${data?.persona?.nombre || ""} ${data?.persona?.apellido || ""}`.trim() || "Chofer";
  const heroAppEmail = data?.chofer?.email || data?.persona?.email || "";
  const heroAppCuit = data?.chofer?.cuit || data?.cuit || cuit;
  const loginInfo = data?.login_info || null;
  const loginMethodFeatureEnabled = CHOFER_LOGIN_METHODS_ENABLED;
  const hasLoginAccess = Boolean(loginInfo?.email_verified && loginInfo.status === "active");
  const showLoginGuard = Boolean(data && !hasLoginAccess);
  const [whatsappPanelVisible, setWhatsappPanelVisible] = useState(false);
  const whatsappPanelRef = useRef(null);
  const openWhatsappPanel = () => {
    setWhatsappPanelVisible((current) => {
      const next = !current;
      if (next) {
        setTimeout(() => whatsappPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
      return next;
    });
  };
  const reopenWhatsappPanel = () => {
    setWhatsappStage("idle");
    setWhatsappCode("");
    setWhatsappMessage("");
    setWhatsappMockCode("");
    setWhatsappResendVisible(false);
    setWhatsappPanelVisible(true);
    setTimeout(() => whatsappPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const heroAppAside = (
    <ChoferSessionSummary
      name={heroAppName}
      cuit={heroAppCuit}
      email={heroAppEmail}
      loginInfo={loginInfo}
      fallbackLoginMethod="email"
      fallbackLoginStatus={showLoginGuard ? "Sin acceso" : "Activo"}
    />
  );
  const loginMethodsList = data?.metodos_acceso || [];
  const whatsappAccessMethod = loginMethodsList.find((method) => method?.tipo === "whatsapp" && !method.expired);
  const effectiveWhatsappPhone =
    chofer?.telefono ||
    whatsappAccessMethod?.metadata?.phone_number ||
    whatsappPhone;
  const whatsappVerified = Boolean(whatsappAccessMethod);
  const heroAction = loginMethodFeatureEnabled ? (
    <div className="hero-action-stack">
      <div className="hero-action-row">
        <button
          className={`secondary-button hero-action-whatsapp-button ${whatsappVerified ? "hero-action-static-button" : ""}`}
          type="button"
          onClick={whatsappVerified ? undefined : openWhatsappPanel}
          aria-disabled={whatsappVerified ? "true" : "false"}
        >
          <span className="hero-action-button-content">
            <span className="whatsapp-button-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                <path d="M19.05 4.95A9.9 9.9 0 0 0 12.02 2a10 10 0 0 0-8.67 15l-1.29 4.7 4.82-1.26A10 10 0 1 0 19.05 4.95Zm-7.03 15.4a8.3 8.3 0 0 1-4.22-1.15l-.3-.18-2.86.75.77-2.8-.2-.3a8.33 8.33 0 1 1 6.8 3.68Zm4.57-6.14c-.25-.13-1.47-.72-1.7-.8-.23-.08-.4-.13-.57.12-.17.25-.65.8-.8.96-.15.17-.3.19-.56.06-.25-.13-1.07-.39-2.04-1.24-.75-.66-1.25-1.47-1.4-1.72-.15-.25-.02-.38.11-.5.12-.12.25-.3.38-.44.13-.15.17-.25.25-.42.08-.17.04-.32-.02-.44-.06-.13-.57-1.38-.78-1.89-.2-.49-.4-.42-.57-.43h-.49c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.43 1.03 2.6.13.17 1.76 2.7 4.27 3.78.6.26 1.07.42 1.43.54.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.17-.48-.3Z" />
              </svg>
            </span>
            <span className="hero-action-button-copy">
              {whatsappVerified ? (
                <>
                  <span className="hero-action-button-title">{effectiveWhatsappPhone || "Número validado"}</span>
                  <span className="hero-action-button-status">Validado</span>
                </>
              ) : (
                <span className="hero-action-button-title">
                  {whatsappPanelVisible ? "Ocultar WhatsApp" : "WhatsApp · Validá tu número"}
                </span>
              )}
            </span>
          </span>
        </button>
        {whatsappVerified ? (
          <button
            className="secondary-button hero-action-inline-button"
            type="button"
            disabled={whatsappLoading}
            onClick={reopenWhatsappPanel}
          >
            Actualizar número
          </button>
        ) : null}
      </div>
    </div>
  ) : null;
  const heroAppTitle = (
    <>
      <span>App personal</span>
      <span>del chofer</span>
    </>
  );

  useEffect(() => {
    setWhatsappPhone(effectiveWhatsappPhone || "");
    if (whatsappAccessMethod) {
      setWhatsappStage("verified");
    }
  }, [effectiveWhatsappPhone, whatsappAccessMethod]);

  return (
    <PortalLayout
      {...choferHeroLayout}
      eyebrow="Portal Chofer"
      title={heroAppTitle}
      description="Resumen de identidad validada, biometria, documentos personales y permisos a empresas."
      backHref={getChoferAppHomeHash(cuit)}
      heroAside={heroAppAside}
      heroAction={heroAction}
    >
      {data ? (
        <>
          {whatsappMessage && !whatsappPanelVisible ? <StatusMessage message={whatsappMessage} /> : null}
          {whatsappPanelVisible ? (
          <section className="detail-panel detail-panel-wide" ref={whatsappPanelRef}>
            <div className="detail-panel-header">
              <h2>WhatsApp</h2>
              <div className="detail-panel-header-actions stacked">
                <StatusBadge
                  label={whatsappAccessMethod ? "Validado" : "Pendiente"}
                  tone={whatsappAccessMethod ? "vigente" : "faltante"}
                />
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setWhatsappPanelVisible(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
            <div className="panel-body">
              <div className="portal-list">
                <PortalListItem
                  title={whatsappAccessMethod ? (effectiveWhatsappPhone || "Número validado") : "Validá tu número"}
                  description={whatsappAccessMethod ? "Validado" : "Asociá y validá tu número de WhatsApp."}
                />
              </div>
              <div className="inline-action-field">
                <Input
                  label={whatsappStage === "pending" ? "Código de WhatsApp" : "Teléfono WhatsApp"}
                  value={whatsappStage === "pending" ? whatsappCode : whatsappPhone}
                  onChange={(value) => {
                    if (whatsappStage === "pending") {
                      setWhatsappCode(value);
                      return;
                    }
                    setWhatsappPhone(value);
                    if (whatsappStage === "verified") {
                      setWhatsappStage("idle");
                    }
                  }}
                  autoComplete={whatsappStage === "pending" ? "one-time-code" : "tel"}
                  placeholder={whatsappStage === "pending" ? "Ingresá el código de 6 dígitos" : "+5491123456789"}
                />
                <button
                  className="submit-button inline-action-field-button"
                  type="button"
                  disabled={whatsappLoading}
                  onClick={whatsappStage === "pending" ? confirmChoferWhatsappVerification : requestChoferWhatsappVerification}
                >
                  {whatsappLoading
                    ? (whatsappStage === "pending" ? "Verificando..." : "Enviando...")
                    : (whatsappStage === "pending" ? "Confirmar código" : "Confirmar teléfono")}
                </button>
                {whatsappStage === "pending" && whatsappResendVisible ? (
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={whatsappLoading}
                    onClick={requestChoferWhatsappVerification}
                  >
                    Reenviar código
                  </button>
                ) : null}
              </div>
              {whatsappMockCode ? <StatusMessage message={`Código mock: ${whatsappMockCode}`} /> : null}
              {whatsappMessage ? <StatusMessage message={whatsappMessage} /> : null}
            </div>
          </section>
          ) : null}
          <section className="portal-section-grid">
              <ExpandableInfoCard
                title="Identidad"
                summary={`${data.persona?.nombre || "-"} ${data.persona?.apellido || ""}`.trim()}
                rows={buildRows(data.persona || { cuit: data.cuit }, ["cuit", "nombre", "apellido", "nro_dni", "fecha_nacimiento"])}
              />
              <ExpandableInfoCard
                title="Biometria"
                summary={data.biometria?.validado ? "Validacion exitosa" : "Sin validacion reciente"}
                badge={
                  data.biometria ? (
                    <StatusBadge label={data.biometria.validado ? "Validado" : "Observado"} tone={data.biometria.validado ? "vigente" : "vencido"} />
                  ) : null
                }
                rows={buildRows(
                  data.biometria || { estado: "Sin validacion reciente" },
                  ["chofer_cuit", "similitud", "validado", "created_at"],
                  { created_at: "Fecha de validacion" }
                )}
              />
          <section
            className="detail-panel detail-panel-clickable"
            role="button"
            tabIndex={0}
            onClick={() => {
              window.location.hash = `#/portal-chofer/app/${cuit}/empresas-vinculadas`;
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                window.location.hash = `#/portal-chofer/app/${cuit}/empresas-vinculadas`;
              }
            }}
          >
          <div className="detail-panel-header">
            <div>
              <h2>Empresas con las que trabajo</h2>
            </div>
            <div className="detail-panel-header-actions stacked">
              <StatusBadge label={data.transport_company ? "Activa" : "Sin vínculo"} tone={data.transport_company ? "vigente" : "pendiente"} />
            </div>
          </div>
            <div className="panel-body">
              {data.transport_company ? (
                <div className="portal-list">
                  <PortalListItem
                    title={data.transport_company.razon_social || `Empresa ${data.transport_company.cuit}`}
                    description={`CUIT ${data.transport_company.cuit || "-"} · Relación activa con la empresa de transporte`}
                  />
                </div>
              ) : (
                <p className="message">
                  Este chofer todavía no tiene una empresa de transporte asociada. Podés agregar el CUIT de la empresa que actúa como transportista o registrarte como autónomo para que la misma cuenta sea la empresa.
                </p>
              )}
              <button
                className="secondary-button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  window.location.hash = `#/portal-chofer/app/${cuit}/empresas-vinculadas`;
                }}
              >
                Ver relación con empresas
              </button>
            </div>
          </section>
          <section
            className="detail-panel detail-panel-clickable"
            role="button"
            tabIndex={0}
            onClick={() => {
              window.location.hash = `#/portal-chofer/app/${cuit}/solicitudes`;
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                window.location.hash = `#/portal-chofer/app/${cuit}/solicitudes`;
              }
            }}
          >
          <div className="detail-panel-header">
            <h2>Solicitudes</h2>
            <div className="detail-panel-header-actions stacked">
              {pendingNotificationsCount ? <span className="notification-bubble">{pendingNotificationsCount}</span> : null}
              <StatusBadge
                label={pendingNotificationsCount ? "Pendientes" : "Sin pendientes"}
                tone={pendingNotificationsCount ? "faltante" : "vigente"}
              />
            </div>
          </div>
          <div className="panel-body">
            {linkRequestsLoading ? (
              <p className="message">Buscando solicitudes...</p>
            ) : pendingNotificationsCount ? (
              <p className="message">{pendingNotificationsCount} solicitudes pendientes</p>
            ) : (
              <p className="message">No hay solicitudes pendientes.</p>
            )}
            <button
              className="secondary-button"
              type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  window.location.hash = `#/portal-chofer/app/${cuit}/solicitudes`;
                }}
              >
                Ver solicitudes
              </button>
            </div>
          </section>
          </section>
          <DocumentsStatusPanel
            chofer={data.chofer}
            biometria={data.biometria}
            documentDefinitions={documentDefinitions}
            documentsByType={documentsByType}
          />

          <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Documentos</h2>
            </div>
            <div className="panel-body">
              <div className="document-card-list">
                {documentDefinitions.map((definition) => {
                  if (definition.tipo === "dni") {
                    const dniFrente = documentsByType.dni_frente || null;
                    const dniDorso = documentsByType.dni_dorso || null;
                    const status = buildDniDocumentCardStatus(dniFrente, dniDorso);
                    const frentePreviewUrl = dniFrente ? documentPreviewUrls[dniFrente.id] || "" : "";
                    const dorsoPreviewUrl = dniDorso ? documentPreviewUrls[dniDorso.id] || "" : "";
                    const isCapturingFrente = activeDocumentCapture === "dni_frente";
                    const isCapturingDorso = activeDocumentCapture === "dni_dorso";

                    return (
                      <details className="detail-panel expandable-card" key={definition.tipo}>
                        <summary className="expandable-card-summary">
                          <div>
                            <h2>{definition.title}</h2>
                            <p>{status.summary}</p>
                          </div>
                          <div className="expandable-card-summary-side">
                            <StatusBadge label={status.label} tone={status.tone} />
                            <span className="expandable-card-chevron" aria-hidden="true">
                              ▾
                            </span>
                          </div>
                        </summary>
                        <div className="expandable-card-body document-card-body">
                          <div className="dni-document-grid">
                            <div className="dni-document-slot">
                              <strong>DNI frente</strong>
                              {isCapturingFrente ? (
                                <CameraCapture
                                  mode="documento"
                                  title="DNI frente"
                                  description=""
                                  onConfirm={(file) => uploadDocumentFromCapture("dni_frente", file)}
                                />
                              ) : frentePreviewUrl ? (
                                <button className="document-card-thumb document-card-thumb-button" type="button" onClick={() => openDocument(dniFrente.id)}>
                                  <img src={frentePreviewUrl} alt="DNI frente" />
                                </button>
                              ) : (
                                <div className="document-card-thumb">
                                  <div className="document-card-empty">
                                    <span>Sin foto</span>
                                  </div>
                                </div>
                              )}
                              {!isCapturingFrente ? (
                                <div className="document-card-actions">
                                  {!dniFrente ? (
                                    <button
                                      className="submit-button"
                                      type="button"
                                      onClick={() => {
                                        setActiveDocumentPreview("");
                                        setActiveDocumentCapture("dni_frente");
                                      }}
                                    >
                                      Subir frente
                                    </button>
                                  ) : null}
                                  {dniFrente ? (
                                    <button
                                      className="secondary-button"
                                      type="button"
                                      onClick={() => openDocument(dniFrente.id)}
                                      disabled={openingDocumentId === String(dniFrente.id)}
                                    >
                                      {openingDocumentId === String(dniFrente.id) ? "Abriendo..." : "Ver frente"}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>

                            <div className="dni-document-slot">
                              <strong>DNI dorso</strong>
                              {isCapturingDorso ? (
                                <CameraCapture
                                  mode="documento"
                                  title="DNI dorso"
                                  description=""
                                  onConfirm={(file) => uploadDocumentFromCapture("dni_dorso", file)}
                                />
                              ) : dorsoPreviewUrl ? (
                                <button className="document-card-thumb document-card-thumb-button" type="button" onClick={() => openDocument(dniDorso.id)}>
                                  <img src={dorsoPreviewUrl} alt="DNI dorso" />
                                </button>
                              ) : (
                                <div className="document-card-thumb">
                                  <div className="document-card-empty">
                                    <span>Sin foto</span>
                                  </div>
                                </div>
                              )}
                              {!isCapturingDorso ? (
                                <div className="document-card-actions">
                                  {!dniDorso ? (
                                    <button
                                      className="submit-button"
                                      type="button"
                                      onClick={() => {
                                        setActiveDocumentPreview("");
                                        setActiveDocumentCapture("dni_dorso");
                                      }}
                                    >
                                      Subir dorso
                                    </button>
                                  ) : null}
                                  {dniDorso ? (
                                    <button
                                      className="secondary-button"
                                      type="button"
                                      onClick={() => openDocument(dniDorso.id)}
                                      disabled={openingDocumentId === String(dniDorso.id)}
                                    >
                                      {openingDocumentId === String(dniDorso.id) ? "Abriendo..." : "Ver dorso"}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          {(isCapturingFrente || isCapturingDorso) && loading ? (
                            <ProcessingPanel title="Procesando foto" message="Estamos guardando el DNI." />
                          ) : null}
                        </div>
                      </details>
                    );
                  }

                  if (definition.tipo === "biometria") {
                    const biometriaStatus = buildBiometriaDocumentCardStatus(data.biometria);
                    return (
                      <details className="detail-panel expandable-card" key={definition.tipo}>
                        <summary className="expandable-card-summary">
                          <div>
                            <h2>{definition.title}</h2>
                            <p>{biometriaStatus.summary}</p>
                          </div>
                          <div className="expandable-card-summary-side">
                            <StatusBadge label={biometriaStatus.label} tone={biometriaStatus.tone} />
                            <span className="expandable-card-chevron" aria-hidden="true">
                              ▾
                            </span>
                          </div>
                        </summary>
                        <div className="expandable-card-body document-card-body">
                          {data.biometria ? (
                            <div className="detail-table">
                              <div className="detail-row">
                                <span>Resultado</span>
                                <strong>{data.biometria.validado ? "Validada" : "Observada"}</strong>
                              </div>
                              <div className="detail-row">
                                <span>Similaridad</span>
                                <strong>{data.biometria.similitud ? `${Number(data.biometria.similitud).toFixed(2)}%` : "-"}</strong>
                              </div>
                              <div className="detail-row">
                                <span>Fecha de validación</span>
                                <strong>{formatDate(data.biometria.created_at)}</strong>
                              </div>
                            </div>
                          ) : (
                            <p className="capture-flow-empty">Todavía no hay validación biométrica registrada.</p>
                          )}
                        </div>
                      </details>
                    );
                  }

                  const documento = documentsByType[definition.tipo] || null;
                  const isLicencia = definition.tipo === "licencia";
                  const status = isLicencia ? buildLicenciaDocumentCardStatus(data.chofer, documento) : buildDocumentCardStatus(documento);
                  const previewUrl = documento ? documentPreviewUrls[documento.id] || "" : "";
                  const isCapturing = activeDocumentCapture === definition.tipo;

                  return (
                    <details className="detail-panel expandable-card" key={definition.tipo}>
                      <summary className="expandable-card-summary">
                        <div>
                          <h2>{definition.title}</h2>
                          <p>
                            {isCapturing
                              ? ""
                              : documento?.fecha_vencimiento
                                ? `Vencimiento ${formatDate(documento.fecha_vencimiento)}`
                                : documento?.created_at
                                  ? `Fecha de alta ${formatDate(documento.created_at)}`
                                  : "Sin foto cargada"}
                          </p>
                        </div>
                        <div className="expandable-card-summary-side">
                          <StatusBadge label={status.label} tone={status.tone} />
                          <span className="expandable-card-chevron" aria-hidden="true">
                            ▾
                          </span>
                        </div>
                      </summary>
                      <div className="expandable-card-body document-card-body">
                        {isCapturing ? (
                          <CameraCapture
                            mode="documento"
                            title={definition.title}
                            description="Captura el documento con buena luz. Al confirmar, lo procesamos automaticamente."
                            onConfirm={(file) =>
                              definition.tipo === "licencia"
                                ? processLicenciaAndSave(file)
                                : uploadDocumentFromCapture(definition.tipo, file)
                            }
                          />
                        ) : previewUrl ? (
                          <button
                            className={`document-card-thumb document-card-thumb-button ${
                              activeDocumentPreview === definition.tipo ? "document-card-thumb-open" : ""
                            }`}
                            type="button"
                            onClick={() =>
                              setActiveDocumentPreview((current) => (current === definition.tipo ? "" : definition.tipo))
                            }
                          >
                            <img src={previewUrl} alt={definition.title} />
                          </button>
                        ) : (
                          <div className="document-card-thumb">
                            <div className="document-card-empty">
                              <span>Sin foto</span>
                            </div>
                          </div>
                        )}
                        {previewUrl && activeDocumentPreview === definition.tipo && !isCapturing ? (
                          <div className="document-card-preview-large">
                            <img src={previewUrl} alt={`${definition.title} ampliada`} />
                          </div>
                        ) : null}
                        <p>{status.reason}</p>
                        {definition.tipo === "licencia" && chofer?.licencia_clases?.length ? (
                          <p className="document-card-license-classes">
                            Clases registradas: {chofer.licencia_clases.join(", ")}
                          </p>
                        ) : null}
                        {definition.tipo === "licencia" && status.tone !== "vigente" && !isCapturing ? (
                          <div className="document-card-license-reupload">
                            <p className="document-card-license-reupload-copy">
                              La licencia no está vigente o falló la validación. Subí otra foto para actualizarla.
                            </p>
                            <button
                              className="secondary-button"
                              type="button"
                              onClick={() => {
                                setActiveDocumentPreview("");
                                setActiveDocumentCapture(definition.tipo);
                              }}
                            >
                              Volver a subir licencia
                            </button>
                          </div>
                        ) : null}
                        {!isCapturing ? (
                          <div className="document-card-actions">
                            <button
                              className={documento ? "secondary-button" : "submit-button"}
                              type="button"
                              onClick={() => {
                                setActiveDocumentPreview("");
                                setActiveDocumentCapture(definition.tipo);
                              }}
                            >
                              {documento ? "Actualizar foto" : "Subir foto"}
                            </button>
                            {documento ? (
                              <button
                                className="secondary-button"
                                type="button"
                                onClick={() => openDocument(documento.id)}
                                disabled={openingDocumentId === String(documento.id)}
                              >
                                {openingDocumentId === String(documento.id) ? "Abriendo..." : "Ver archivo"}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                        {isCapturing && (definition.tipo === "licencia" ? savingLicencia : loading) ? (
                          <ProcessingPanel title="Procesando foto" message="Estamos guardando y validando el documento." />
                        ) : null}
                      </div>
                    </details>
                  );
                })}
              </div>
              {loading ? <ProcessingPanel title="Guardando documento" message="Estamos guardando la foto del documento." /> : null}
            </div>
            </section>
        </>
      ) : null}
    </PortalLayout>
  );
}

export function PortalChoferRequestsScreen({ cuitFromRoute = "" }) {
  const [cuit] = useState(() => cuitFromRoute || window.localStorage.getItem("portalChoferCuit") || "");
  const [message, setMessage] = useState("");
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [signedContracts, setSignedContracts] = useState([]);
  const [unitRegistrationRequests, setUnitRegistrationRequests] = useState([]);
  const [operationDocumentRequests, setOperationDocumentRequests] = useState([]);
  const [linkRequests, setLinkRequests] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [respondingUnitRequestId, setRespondingUnitRequestId] = useState("");
  const [respondingLinkId, setRespondingLinkId] = useState("");
  const [activeUnitRequestId, setActiveUnitRequestId] = useState("");
  const [unitRequestAnalyses, setUnitRequestAnalyses] = useState({});
  const [unitRequestFiles, setUnitRequestFiles] = useState({});
  const [unitRequestFeedback, setUnitRequestFeedback] = useState({});
  const [openingDocumentId, setOpeningDocumentId] = useState("");
  const [activeContractId, setActiveContractId] = useState("");
  const [contractSignerFiles, setContractSignerFiles] = useState({});
  const [signingContractId, setSigningContractId] = useState("");
  const [signingResults, setSigningResults] = useState({});
  const [signatureRequests, setSignatureRequests] = useState({});
  const [signatureDrafts, setSignatureDrafts] = useState({});
  const [applyingSignatureId, setApplyingSignatureId] = useState("");
  const [contractMessages, setContractMessages] = useState({});
  const [choferProfile, setChoferProfile] = useState(null);
  const pendingUnitRegistrationRequests = unitRegistrationRequests.filter((request) => request.status === "pending_acceptance");
  const pendingOperationDocumentRequests = operationDocumentRequests.filter((request) => request.status === "pending");
  const heroRequestsName = `${choferProfile?.persona?.nombre || ""} ${choferProfile?.persona?.apellido || ""}`.trim() || "Chofer";
  const heroRequestsEmail = choferProfile?.chofer?.email || choferProfile?.persona?.email || "";
  const heroRequestsCuit = choferProfile?.cuit || cuit;
  const heroRequestsAside = <ChoferSessionSummary name={heroRequestsName} cuit={heroRequestsCuit} email={heroRequestsEmail} />;
  const heroRequestsTitle = (
    <>
      <span>Solicitudes</span>
      <span>del chofer</span>
    </>
  );

  const refreshRequests = useCallback(async (nextCuit = cuit) => {
    if (!nextCuit) {
      setPendingContracts([]);
      setSignedContracts([]);
      setUnitRegistrationRequests([]);
      setOperationDocumentRequests([]);
      return;
    }

    setLoadingRequests(true);
    try {
      const [pendingResponse, historyResponse, unitRequestResponse, operationDocumentResponse] = await Promise.allSettled([
        getJson(`/portal-chofer/choferes/${nextCuit}/contracts/pending`),
        getJson(`/portal-chofer/choferes/${nextCuit}/contracts/history`),
        getJson(`/portal-chofer/choferes/${nextCuit}/unit-registration-requests`),
        getJson(`/portal-chofer/choferes/${nextCuit}/operation-document-requests`),
      ]);
      setPendingContracts(pendingResponse.status === "fulfilled" ? pendingResponse.value.data || [] : []);
      setSignedContracts(historyResponse.status === "fulfilled" ? historyResponse.value.data || [] : []);
      setUnitRegistrationRequests(unitRequestResponse.status === "fulfilled" ? unitRequestResponse.value.data || [] : []);
      setOperationDocumentRequests(operationDocumentResponse.status === "fulfilled" ? operationDocumentResponse.value.data || [] : []);
      window.localStorage.setItem("portalChoferCuit", nextCuit);
    } catch (error) {
      setPendingContracts([]);
      setSignedContracts([]);
      setUnitRegistrationRequests([]);
      setOperationDocumentRequests([]);
      setMessage(error.message);
    } finally {
      setLoadingRequests(false);
    }
  }, [cuit]);

  const loadLinkRequests = useCallback(async (nextCuit = cuit) => {
    if (!nextCuit) {
      setLinkRequests([]);
      return;
    }

    setLinksLoading(true);
    try {
      const response = await getJson(`/portal-chofer/choferes/${nextCuit}/transport-links`);
      const pending = (response.data || []).filter((link) => link.status === "pending");
      setLinkRequests(pending);
    } catch (_error) {
      setLinkRequests([]);
    } finally {
      setLinksLoading(false);
    }
  }, [cuit]);

  useEffect(() => {
    refreshRequests(cuit);
  }, [cuit, refreshRequests]);

  useEffect(() => {
    loadLinkRequests(cuit);
  }, [cuit, loadLinkRequests]);

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      if (!cuit) {
        setChoferProfile(null);
        return;
      }

      try {
        const response = await getJson(`/identidad/personas/${cuit}`);
        if (!cancelled) {
          setChoferProfile(response.data);
        }
      } catch (_error) {
        if (!cancelled) {
          setChoferProfile(null);
        }
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [cuit]);

  async function respondUnitRegistrationRequest(requestId, status) {
    setRespondingUnitRequestId(String(requestId));
    setMessage("");
    try {
      await patchJson(`/portal-chofer/unit-registration-requests/${requestId}`, { status });
      await refreshRequests();
      setMessage(status === "approved" ? "Solicitud de unidad aceptada." : "Solicitud de unidad rechazada.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRespondingUnitRequestId("");
    }
  }

  async function respondTransportLink(linkId, status) {
    setRespondingLinkId(String(linkId));
    setMessage("");
    try {
      await patchJson(`/portal-chofer/transport-links/${linkId}`, { status });
      await loadLinkRequests();
      await refreshRequests();
      setLinkRequests((current) => current.filter((link) => String(link.id) !== String(linkId)));
      setMessage(status === "approved" ? "Vínculo aceptado." : "Vínculo rechazado.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRespondingLinkId("");
    }
  }

  function openUnitRegistrationFlow(request) {
    setActiveUnitRequestId(String(request.id));
    setUnitRequestFeedback((current) => ({
      ...current,
      [request.id]: {
        stage: "capture",
        message: "Captura la cédula para validar dominio y tipo de unidad.",
        error: false,
      },
    }));
    setMessage("");
  }

  async function analyzeUnitRegistrationCapture(request, file) {
    const expectedEntityType = request.unit_type === "tractor_unit" ? "unidad" : "acoplado";

    setUnitRequestFeedback((current) => ({
      ...current,
      [request.id]: {
        stage: "analyzing",
        message: "Validando dominio y tipo de unidad en la cédula.",
        error: false,
      },
    }));
    setMessage("");

    try {
      const response = await postForm(
        "/documentos/analizar",
        {
          entidad_tipo: expectedEntityType,
          tipo_sugerido: "cedula_frente",
        },
        { archivo: file },
        {
          timeoutMs: 60000,
          timeoutMessage: "El análisis de la cédula demoró demasiado. Reintenta la captura.",
        }
      );

      const analysis = response.data || {};
      const detectedDomain = String(analysis?.datos_extraidos?.dominio || "").toUpperCase();
      const requestedDomain = String(request.domain || "").toUpperCase();
      const detectedEntityType = resolveDetectedEntityType(analysis);

      if (!detectedDomain) {
        throw new Error("No pudimos detectar el dominio en la foto de la cédula.");
      }

      if (detectedDomain !== requestedDomain) {
        throw new Error(`El dominio detectado ${detectedDomain} no coincide con la solicitud ${requestedDomain}.`);
      }

      if (detectedEntityType !== expectedEntityType) {
        throw new Error(
          expectedEntityType === "unidad"
            ? "La foto corresponde a un remolque. Debe coincidir con una unidad tractora."
            : "La foto corresponde a una tractora. Debe coincidir con una unidad remolque."
        );
      }

      setUnitRequestFiles((current) => ({
        ...current,
        [request.id]: file,
      }));
      setUnitRequestAnalyses((current) => ({
        ...current,
        [request.id]: analysis,
      }));
      setUnitRequestFeedback((current) => ({
        ...current,
        [request.id]: {
          stage: "validated",
          message: `Cédula validada para ${requestedDomain}. Ya puedes guardar la entidad.`,
          error: false,
        },
      }));
      setMessage(`Cédula validada para ${requestedDomain}. Ya puedes guardar la entidad.`);
    } catch (error) {
      setUnitRequestFiles((current) => {
        const next = { ...current };
        delete next[request.id];
        return next;
      });
      setUnitRequestAnalyses((current) => {
        const next = { ...current };
        delete next[request.id];
        return next;
      });
      setUnitRequestFeedback((current) => ({
        ...current,
        [request.id]: {
          stage: "error",
          message: error.message,
          error: true,
        },
      }));
      setMessage(error.message);
    } finally {
      // no-op
    }
  }

  async function submitUnitRegistrationRequest(request) {
    const analysis = unitRequestAnalyses[request.id];
    const file = unitRequestFiles[request.id];
    const expectedEntityType = request.unit_type === "tractor_unit" ? "unidad" : "acoplado";

    if (!analysis || !file) {
      setMessage("Primero valida la foto de la cédula para continuar.");
      return;
    }

    if (!request.transport_company_id) {
      setMessage("La solicitud no tiene una empresa de transporte asociada para guardar la unidad.");
      return;
    }

    const endpoint = expectedEntityType === "unidad" ? "/portal-transporte/unidades" : "/portal-transporte/acoplados";

    setRespondingUnitRequestId(String(request.id));
    setUnitRequestFeedback((current) => ({
      ...current,
      [request.id]: {
        stage: "saving",
        message: "Guardando la entidad y cerrando la solicitud.",
        error: false,
      },
    }));
    setMessage("");

    try {
      await postForm(
        endpoint,
        {
          titular_cuit: request.transport_company_id,
          dominio: analysis?.datos_extraidos?.dominio || request.domain,
          marca: analysis?.datos_extraidos?.marca || "",
          modelo: analysis?.datos_extraidos?.modelo || "",
          tipo: analysis?.datos_extraidos?.tipo || (expectedEntityType === "unidad" ? "Tractor" : "Remolque"),
          expected_unit_type: request.unit_type,
        },
        { cedula_frente: file }
      );
      setActiveUnitRequestId("");
      setUnitRequestFiles((current) => {
        const next = { ...current };
        delete next[request.id];
        return next;
      });
      setUnitRequestAnalyses((current) => {
        const next = { ...current };
        delete next[request.id];
        return next;
      });
      setUnitRequestFeedback((current) => {
        const next = { ...current };
        delete next[request.id];
        return next;
      });
      await refreshRequests();
      setMessage(`${request.unit_type === "tractor_unit" ? "Unidad tractora" : "Unidad remolque"} ${request.domain} dada de alta correctamente.`);
    } catch (error) {
      setUnitRequestFeedback((current) => ({
        ...current,
        [request.id]: {
          stage: "error",
          message: error.message,
          error: true,
        },
      }));
      setMessage(error.message);
    } finally {
      setRespondingUnitRequestId("");
    }
  }

  async function loadSignatureRequest(documentId) {
    const response = await getJson(`/signatures/documents/${documentId}/signature-request?viewer_type=chofer&viewer_id=${cuit}`);
    setSignatureRequests((current) => ({
      ...current,
      [documentId]: response.data,
    }));
  }

  async function openContractDocument(documentId) {
    setOpeningDocumentId(String(documentId));
    try {
      const response = await getJson(`/signatures/documents/${documentId}/url`);
      window.open(response.data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setOpeningDocumentId("");
    }
  }

  async function signPendingContract(contractId) {
    const files = contractSignerFiles[contractId] || {};

    if (!files.dni_frente || !files.selfie) {
      setMessage("Completa DNI frente y selfie para firmar el documento.");
      return;
    }

    setSigningContractId(String(contractId));
    setMessage("");
    setContractMessages((current) => ({ ...current, [contractId]: "" }));

    try {
      const response = await postForm(
        `/portal-chofer/contracts/${contractId}/sign`,
        {},
        {
          dni_frente: files.dni_frente,
          selfie: files.selfie,
        },
        {
          timeoutMs: 120000,
          timeoutMessage: "La firma está tardando más de lo esperado. Reintenta en unos segundos.",
        }
      );

      setSigningResults((current) => ({
        ...current,
        [contractId]: response.data,
      }));
      await refreshRequests();
      await loadSignatureRequest(contractId);
      setContractMessages((current) => ({
        ...current,
        [contractId]: response.data?.biometric?.validado
          ? "Biometría aprobada. Ya puedes aplicar la firma al PDF."
          : "La biometría quedó observada. Reintenta la validación.",
      }));
      setMessage(
        response.data?.biometric?.validado
          ? "Biometría aprobada. Ahora firma el documento."
          : "La biometría quedó observada. Reintenta la validación."
      );
    } catch (error) {
      setContractMessages((current) => ({ ...current, [contractId]: error.message }));
      setMessage(error.message);
    } finally {
      setSigningContractId("");
    }
  }

  async function applyContractSignature(contractId) {
    const signatureDraft = signatureDrafts[contractId];
    const biometricResult = signingResults[contractId];
    const signatureRequest = signatureRequests[contractId];
    const placeholder = signatureRequest?.placeholders?.find((item) => item.status === "pending");

    if (!signatureDraft || !placeholder || !biometricResult?.biometric_validation_id) {
      setContractMessages((current) => ({
        ...current,
        [contractId]: "Primero valida biometría y confirma la firma manuscrita.",
      }));
      setMessage("Primero valida biometría y confirma la firma manuscrita.");
      return;
    }

    setApplyingSignatureId(String(contractId));
    setMessage("");
    setContractMessages((current) => ({ ...current, [contractId]: "Aplicando firma al PDF..." }));

    try {
      const response = await postJson(
        `/signatures/documents/${contractId}/sign`,
        {
          placeholder_id: placeholder.id,
          signature_image_base64: signatureDraft.signature_image_base64,
          biometric_validation_id: biometricResult.biometric_validation_id,
          viewer_type: "chofer",
          viewer_id: cuit,
          add_visible_metadata: true,
        },
        {
          timeoutMs: 120000,
          timeoutMessage: "La firma visual está tardando más de lo esperado.",
        }
      );

      setSignatureDrafts((current) => {
        const next = { ...current };
        delete next[contractId];
        return next;
      });
      setSigningResults((current) => {
        const next = { ...current };
        delete next[contractId];
        return next;
      });
      await refreshRequests();
      await loadSignatureRequest(contractId).catch(() => null);
      setContractMessages((current) => ({
        ...current,
        [contractId]: `Documento firmado. Estado final: ${humanizeLabel(response.data.status)}.`,
      }));
      setMessage(`Documento firmado. Estado final: ${humanizeLabel(response.data.status)}.`);
    } catch (error) {
      setContractMessages((current) => ({ ...current, [contractId]: error.message }));
      setMessage(error.message);
    } finally {
      setApplyingSignatureId("");
    }
  }

  return (
    <PortalLayout
      {...choferHeroLayout}
      eyebrow="Portal Chofer"
      title={heroRequestsTitle}
      description="Acepta habilitaciones, responde pedidos de acceso y firma contratos pendientes."
      backHref={getChoferAppHomeHash(cuit)}
      heroAside={heroRequestsAside}
    >
      <StatusMessage message={message} />
      <section className="portal-section-grid">
      <section className="detail-panel detail-panel-wide">
        <div className="detail-panel-header">
          <h2>Solicitudes de vinculación</h2>
        </div>
        <div className="panel-body">
          {linksLoading ? (
            <ProcessingPanel title="Buscando vínculos" message="Estamos cargando las solicitudes de empresa." />
          ) : null}
          {!linksLoading && !linkRequests.length ? (
            <div className="portal-list">
              <PortalListItem
                title="Sin solicitudes"
                description="No hay pedidos pendientes de vínculo con una empresa de transporte."
              />
            </div>
          ) : null}
          <div className="contract-list">
            {linkRequests.map((link) => (
              <details className="detail-panel expandable-card request-card" key={`chofer-transport-link-request-${link.id}`}>
                <summary className="expandable-card-summary">
                  <div>
                    <h2>{link.transport_company_name || `Empresa ${link.transport_company_cuit}`}</h2>
                    <p>Status: Pendiente</p>
                  </div>
                  <div className="expandable-card-summary-side">
                    <StatusBadge label="Pendiente" tone="pendiente" />
                    <span className="expandable-card-chevron" aria-hidden="true">▾</span>
                  </div>
                </summary>
                <div className="expandable-card-body">
                  <div className="detail-table">
                    <div className="detail-row">
                      <span>CUIT empresa</span>
                      <strong>{link.transport_company_cuit || "-"}</strong>
                    </div>
                    <div className="detail-row">
                      <span>Solicitado por</span>
                      <strong>{humanizeLabel(link.requested_by_type)}</strong>
                    </div>
                    <div className="detail-row">
                      <span>Solicitado</span>
                      <strong>{formatDate(link.created_at)}</strong>
                    </div>
                  </div>
                  <div className="biometry-actions">
                    <button
                      className="submit-button"
                      type="button"
                      disabled={respondingLinkId === String(link.id)}
                      onClick={() => respondTransportLink(link.id, "approved")}
                    >
                      {respondingLinkId === String(link.id) ? "Procesando..." : "Aceptar vínculo"}
                    </button>
                    <button
                      className="secondary-button"
                      type="button"
                      disabled={respondingLinkId === String(link.id)}
                      onClick={() => respondTransportLink(link.id, "revoked")}
                    >
                      Rechazar vínculo
                    </button>
                  </div>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>Solicitudes de documentación</h2>
          </div>
          <div className="panel-body">
            {!pendingOperationDocumentRequests.length ? (
              <div className="portal-list">
                <PortalListItem title="Sin solicitudes" description="No hay pedidos de documentos pendientes para este chofer." />
              </div>
            ) : (
              <div className="contract-list">
                {pendingOperationDocumentRequests.map((request) => (
                  <details className="detail-panel expandable-card request-card" key={`chofer-operation-document-request-${request.id}`}>
                    <summary className="expandable-card-summary">
                      <div>
                        <h2>{getOperationDocumentRequestLabel(request)}</h2>
                        <p>
                          {request.consumer_company_name
                            ? `La empresa ${request.consumer_company_name} necesita este documento para hacer efectiva la operación.`
                            : "Hay un documento pendiente para hacer efectiva la operación."}
                        </p>
                      </div>
                      <div className="expandable-card-summary-side">
                        <StatusBadge label="Pendiente" tone="faltante" />
                        <span className="expandable-card-chevron" aria-hidden="true">▾</span>
                      </div>
                    </summary>
                    <div className="expandable-card-body">
                      <div className="detail-table">
                        <div className="detail-row">
                          <span>Documento pendiente</span>
                          <strong>{getOperationDocumentRequestLabel(request)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Empresa consumidora</span>
                          <strong>{request.consumer_company_name || "-"}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Operación</span>
                          <strong>{request.operation_id}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Solicitado</span>
                          <strong>{formatDate(request.created_at)}</strong>
                        </div>
                      </div>
                      <p className="mini-copy">{request.request_reason || "Cargá este documento para completar la operación."}</p>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </section>
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>Solicitudes de alta de unidades</h2>
          </div>
          <div className="panel-body">
            {!pendingUnitRegistrationRequests.length ? (
              <div className="portal-list">
                <PortalListItem title="Sin solicitudes" description="No hay nuevas solicitudes de alta de unidades para este chofer." />
              </div>
            ) : (
              <div className="contract-list">
                {pendingUnitRegistrationRequests.map((request) => {
                  const isActive = activeUnitRequestId === String(request.id);
                  const analysis = unitRequestAnalyses[request.id];
                  const feedback = unitRequestFeedback[request.id];

                  return (
                  <details className="detail-panel expandable-card request-card" key={`chofer-unit-request-${request.id}`}>
                    <summary className="expandable-card-summary">
                      <div>
                        <h2>
                          {request.request_kind === "document"
                            ? `${getUnitDocumentLabel(request.document_type)} de ${request.unit_type === "tractor_unit" ? "unidad tractora" : "unidad remolque"}`
                            : request.unit_type === "tractor_unit"
                              ? "Alta de unidad tractora"
                              : "Alta de unidad remolque"}
                        </h2>
                        <p>
                          {request.request_kind === "document"
                            ? `${request.consumer_company_name ? `La empresa ${request.consumer_company_name}` : "La empresa consumidora"} solicita ${getUnitDocumentLabel(request.document_type).toLowerCase()} para el dominio ${request.domain}.`
                            : request.consumer_company_name
                              ? `La empresa ${request.consumer_company_name} solicita el alta del dominio ${request.domain}.`
                              : request.domain}
                        </p>
                      </div>
                      <div className="expandable-card-summary-side">
                        <StatusBadge label={humanizeLabel(request.status)} tone={mapAccessTone(request.status)} />
                        <span className="expandable-card-chevron" aria-hidden="true">▾</span>
                      </div>
                    </summary>
                    <div className="expandable-card-body">
                      <div className="detail-table">
                        <div className="detail-row">
                          <span>Dominio</span>
                          <strong>{request.domain}</strong>
                        </div>
                        {request.request_kind === "document" ? (
                          <div className="detail-row">
                            <span>Documento solicitado</span>
                            <strong>{getUnitDocumentLabel(request.document_type)}</strong>
                          </div>
                        ) : null}
                        <div className="detail-row">
                          <span>Empresa consumidora</span>
                          <strong>{request.consumer_company_name || "-"}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Empresa de transporte</span>
                          <strong>{request.transport_company_id || "-"}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Solicitado</span>
                          <strong>{formatDate(request.requested_at || request.created_at)}</strong>
                        </div>
                      </div>
                      <div className="biometry-actions">
                        <button
                          className="submit-button"
                          type="button"
                          disabled={respondingUnitRequestId === String(request.id)}
                          onClick={() => {
                            if (request.request_kind === "document") {
                              setMessage(
                                `La carga de ${getUnitDocumentLabel(request.document_type).toLowerCase()} debe resolverse desde la empresa de transporte para el dominio ${request.domain}.`
                              );
                              return;
                            }
                            openUnitRegistrationFlow(request);
                          }}
                        >
                          {request.request_kind === "document" ? "Resolver solicitud" : "Dar de alta"}
                        </button>
                        <button
                          className="secondary-button"
                          type="button"
                          disabled={respondingUnitRequestId === String(request.id)}
                          onClick={() => respondUnitRegistrationRequest(request.id, "rejected")}
                        >
                          {respondingUnitRequestId === String(request.id) ? "Procesando..." : "Rechazar"}
                        </button>
                      </div>
                      {feedback?.stage === "saving" ? (
                        <ProcessingPanel
                          title="Guardando entidad"
                          message={feedback.message || "Estamos dando de alta la unidad y cerrando la solicitud."}
                        />
                      ) : null}
                      {isActive && request.request_kind !== "document" ? (
                        <div className="panel-body">
                          {!analysis ? (
                            <>
                              <p>Captura la cédula. El dominio y el tipo detectados deben coincidir con la solicitud.</p>
                              <CameraCapture
                                mode="documento"
                                title="Cédula de la unidad"
                                description=""
                                onConfirm={(file) => analyzeUnitRegistrationCapture(request, file)}
                                processing={feedback?.stage === "analyzing"}
                                processingTitle="Procesando cédula"
                                processingMessage={feedback?.message || "Estamos controlando dominio y tipo de unidad contra la foto."}
                                resultMessage={feedback?.stage === "error" ? feedback.message : ""}
                                resultError={feedback?.stage === "error"}
                              />
                            </>
                          ) : (
                            <>
                              <div className="detail-table">
                                <div className="detail-row">
                                  <span>Dominio detectado</span>
                                  <strong>{analysis?.datos_extraidos?.dominio || "-"}</strong>
                                </div>
                                <div className="detail-row">
                                  <span>Marca</span>
                                  <strong>{analysis?.datos_extraidos?.marca || "-"}</strong>
                                </div>
                                <div className="detail-row">
                                  <span>Modelo</span>
                                  <strong>{analysis?.datos_extraidos?.modelo || "-"}</strong>
                                </div>
                                <div className="detail-row">
                                  <span>Tipo detectado</span>
                                  <strong>{analysis?.datos_extraidos?.tipo || "-"}</strong>
                                </div>
                              </div>
                              <div className="biometry-actions">
                                <button
                                  className="submit-button"
                                  type="button"
                                  disabled={respondingUnitRequestId === String(request.id)}
                                  onClick={() => submitUnitRegistrationRequest(request)}
                                >
                                  {respondingUnitRequestId === String(request.id) ? "Guardando..." : "Guardar entidad"}
                                </button>
                                <button
                                  className="secondary-button"
                                  type="button"
                                  disabled={respondingUnitRequestId === String(request.id)}
                                  onClick={() => {
                                    setUnitRequestFiles((current) => {
                                      const next = { ...current };
                                      delete next[request.id];
                                      return next;
                                    });
                                    setUnitRequestAnalyses((current) => {
                                      const next = { ...current };
                                      delete next[request.id];
                                      return next;
                                    });
                                    setUnitRequestFeedback((current) => ({
                                      ...current,
                                      [request.id]: {
                                        stage: "capture",
                                        message: "Captura una nueva cédula para volver a validar la solicitud.",
                                        error: false,
                                      },
                                    }));
                                  }}
                                >
                                  Repetir foto
                                </button>
                                <button
                                  className="secondary-button"
                                  type="button"
                                  disabled={respondingUnitRequestId === String(request.id)}
                                  onClick={() => {
                                    setActiveUnitRequestId("");
                                    setUnitRequestFeedback((current) => {
                                      const next = { ...current };
                                      delete next[request.id];
                                      return next;
                                    });
                                  }}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </details>
                  );
                })}
              </div>
            )}
          </div>
        </section>
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>Pendientes de firma</h2>
          </div>
          <div className="panel-body">
            {loadingRequests ? <ProcessingPanel title="Cargando contratos" message="Estamos buscando documentos pendientes para firmar." /> : null}
            {!loadingRequests && !pendingContracts.length ? (
              <div className="portal-list">
                <PortalListItem title="Sin pendientes" description="No hay contratos pendientes para este chofer." />
              </div>
            ) : null}
            <div className="contract-list">
              {pendingContracts.map((contract) => {
                const isActive = activeContractId === String(contract.id);
                const signingResult = signingResults[contract.id];
                const contractMessage = contractMessages[contract.id];

                return (
                  <details className="detail-panel expandable-card contract-card" key={`pending-chofer-contract-screen-${contract.id}`} open={isActive}>
                    <summary
                      className="expandable-card-summary"
                      onClick={(event) => {
                        event.preventDefault();
                        const nextId = activeContractId === String(contract.id) ? "" : String(contract.id);
                        setActiveContractId(nextId);
                        if (nextId) {
                          loadSignatureRequest(contract.id).catch((error) => setMessage(error.message));
                        }
                      }}
                    >
                      <div>
                        <h2>{contract.title}</h2>
                        <p>{contract.document_name || "Contrato pendiente"}</p>
                      </div>
                      <div className="expandable-card-summary-side">
                        <StatusBadge label="Pendiente" tone="faltante" />
                        <span className="expandable-card-chevron" aria-hidden="true">▾</span>
                      </div>
                    </summary>
                    <div className="expandable-card-body">
                      <div className="detail-table">
                        <div className="detail-row">
                          <span>Chofer destino</span>
                          <strong>{contract.target_id}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Fecha de alta</span>
                          <strong>{formatDate(contract.created_at)}</strong>
                        </div>
                      </div>
                      <div className="biometry-actions">
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => openContractDocument(contract.id)}
                          disabled={openingDocumentId === String(contract.id)}
                        >
                          {openingDocumentId === String(contract.id) ? "Abriendo..." : "Ver contrato"}
                        </button>
                      </div>
                      <div className="contract-sign-capture-grid">
                        <section className="detail-panel">
                          <div className="detail-panel-header">
                            <h2>DNI frente</h2>
                          </div>
                          <div className="panel-body">
                            <CameraCapture
                              mode="documento"
                              title="DNI frente"
                              description="Captura el frente del DNI para la firma."
                              onConfirm={(file) =>
                                setContractSignerFiles((current) => ({
                                  ...current,
                                  [contract.id]: {
                                    ...(current[contract.id] || {}),
                                    dni_frente: file,
                                  },
                                }))
                              }
                            />
                          </div>
                        </section>
                        <section className="detail-panel">
                          <div className="detail-panel-header">
                            <h2>Selfie</h2>
                          </div>
                          <div className="panel-body">
                            <CameraCapture
                              mode="selfie"
                              title="Selfie"
                              description="Captura una selfie para firmar con biometría."
                              onConfirm={(file) =>
                                setContractSignerFiles((current) => ({
                                  ...current,
                                  [contract.id]: {
                                    ...(current[contract.id] || {}),
                                    selfie: file,
                                  },
                                }))
                              }
                            />
                          </div>
                        </section>
                      </div>
                      <div className="biometry-actions">
                        <button
                          className="submit-button"
                          type="button"
                          onClick={() => signPendingContract(contract.id)}
                          disabled={signingContractId === String(contract.id)}
                        >
                          {signingContractId === String(contract.id) ? "Firmando..." : "Firmar documento"}
                        </button>
                      </div>
                      {signingContractId === String(contract.id) ? (
                        <ProcessingPanel title="Firmando documento" message="Estamos validando identidad y biometría del chofer." />
                      ) : null}
                      {signingResult ? <ContractSignatureResultCard result={signingResult} /> : null}
                      {contractMessage && !signingResult?.biometric?.validado ? <StatusMessage message={contractMessage} /> : null}
                      {signingResult?.biometric?.validado ? (
                        <section className="detail-panel detail-panel-wide">
                          <div className="detail-panel-header">
                            <h2>Firma manuscrita</h2>
                          </div>
                          <div className="panel-body">
                            <SignaturePad
                              title="Firma manuscrita"
                              description="Dibuja tu firma dentro del recuadro."
                              onConfirm={(signature) =>
                                setSignatureDrafts((current) => ({
                                  ...current,
                                  [contract.id]: signature,
                                }))
                              }
                            />
                            <div className="biometry-actions">
                              <button
                                className="submit-button"
                                type="button"
                                onClick={() => applyContractSignature(contract.id)}
                                disabled={applyingSignatureId === String(contract.id) || !signatureDrafts[contract.id]}
                              >
                                {applyingSignatureId === String(contract.id) ? "Aplicando firma..." : "Aplicar firma al PDF"}
                              </button>
                            </div>
                            {applyingSignatureId === String(contract.id) ? (
                              <ProcessingPanel title="Aplicando firma al PDF" message="Estamos generando el PDF firmado." />
                            ) : null}
                            {contractMessage ? <StatusMessage message={contractMessage} /> : null}
                          </div>
                        </section>
                      ) : null}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        </section>
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>Histórico firmado</h2>
          </div>
          <div className="panel-body">
            {!signedContracts.length ? (
              <div className="portal-list">
                <PortalListItem title="Sin historial" description="Todavía no hay contratos firmados por este chofer." />
              </div>
            ) : (
              <div className="contract-list">
                {signedContracts.map((contract) => (
                  <details className="detail-panel expandable-card request-card" key={`chofer-signed-contract-screen-${contract.id}`}>
                    <summary className="expandable-card-summary">
                      <div>
                        <h2>{contract.title}</h2>
                        <p>{contract.document_name || "Contrato firmado"}</p>
                      </div>
                      <div className="expandable-card-summary-side">
                        <StatusBadge
                          label={contract.status === "signed" ? "Firmado" : "Observado"}
                          tone={contract.status === "signed" ? "vigente" : "faltante"}
                        />
                        <span className="expandable-card-chevron" aria-hidden="true">▾</span>
                      </div>
                    </summary>
                    <div className="expandable-card-body">
                      <div className="detail-table">
                        <div className="detail-row">
                          <span>Firmante</span>
                          <strong>{contract.signer_name || "-"}</strong>
                        </div>
                        <div className="detail-row">
                          <span>DNI</span>
                          <strong>{contract.signer_dni || "-"}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Fecha de firma</span>
                          <strong>{formatDate(contract.signed_at || contract.updated_at || contract.created_at)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Similitud biométrica</span>
                          <strong>{contract.biometric_similarity ? `${Number(contract.biometric_similarity).toFixed(2)}%` : "-"}</strong>
                        </div>
                      </div>
                      <div className="biometry-actions">
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => openContractDocument(contract.id)}
                          disabled={openingDocumentId === String(contract.id)}
                        >
                          {openingDocumentId === String(contract.id) ? "Abriendo..." : "Ver documento firmado"}
                        </button>
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </section>
      </section>
    </PortalLayout>
  );
}

function DocumentsStatusPanel({ chofer, biometria, documentDefinitions, documentsByType }) {
  const items = documentDefinitions.map((definition) => {
    let documento = documentsByType[definition.tipo] || null;
    let status;

    if (definition.tipo === "licencia") {
      status = buildLicenciaDocumentCardStatus(chofer, documento);
    } else if (definition.tipo === "dni") {
      const dniFrente = documentsByType.dni_frente || null;
      const dniDorso = documentsByType.dni_dorso || null;
      documento = dniFrente || dniDorso;
      status = buildDniDocumentCardStatus(dniFrente, dniDorso);
    } else if (definition.tipo === "biometria") {
      documento = biometria ? { id: "biometria" } : null;
      status = buildBiometriaDocumentCardStatus(biometria);
    } else {
      status = buildDocumentCardStatus(documento);
    }

    return {
      key: definition.tipo,
      title: definition.title,
      status,
      documento,
    };
  });
  const loadedCount = items.filter((item) => item.documento).length;
  const completionPercentage = items.length ? Math.round((loadedCount / items.length) * 100) : 0;

  return (
    <section className="detail-panel">
      <div className="detail-panel-header">
        <h2>Estado de documentos</h2>
        <strong>{completionPercentage}% completado</strong>
      </div>
      <div className="detail-table">
        <div className="detail-row">
          <span>Documentos cargados</span>
          <strong>{loadedCount} de {items.length}</strong>
        </div>
      </div>
    </section>
  );
}

function ContractSignatureResultCard({ result }) {
  const validated = result?.biometric?.validado;
  return (
    <section className="detail-panel contract-result-card">
      <div className="detail-panel-header">
        <h2>Resultado de firma</h2>
        <StatusBadge label={validated ? "Firmado" : "Observado"} tone={validated ? "vigente" : "faltante"} />
      </div>
      <div className="detail-table">
        <div className="detail-row">
          <span>Similitud biométrica</span>
          <strong>{result?.biometric?.similitud ? `${Number(result.biometric.similitud).toFixed(2)}%` : "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Estado del documento</span>
          <strong>{humanizeLabel(result?.contract?.status || "-")}</strong>
        </div>
      </div>
    </section>
  );
}

function buildLicenciaStatus(chofer, documento = null) {
  const validationStatus = String(documento?.validacion?.status || "").trim().toLowerCase();
  if (validationStatus === "validated" || validationStatus === "approved") {
    return {
      label: "Vigente",
      tone: "vigente",
      reason: "La última licencia cargada quedó validada.",
    };
  }

  if (String(documento?.estado || "").trim().toLowerCase() === "vigente" && !documento?.fecha_vencimiento) {
    return {
      label: "Vigente",
      tone: "vigente",
      reason: "La última licencia cargada figura vigente, aunque no tenga fecha de vencimiento informada.",
    };
  }

  const licenciaNumero = String(chofer?.licencia_numero || "").trim();
  const licenciaVencimiento = String(chofer?.licencia_vencimiento || "").trim();

  if (!licenciaNumero && !licenciaVencimiento) {
    return {
      label: "Faltante",
      tone: "vencido",
      reason: "Todavia no hay licencia operativa cargada.",
    };
  }

  if (!licenciaNumero || !licenciaVencimiento) {
    return {
      label: "Revision",
      tone: "faltante",
      reason: "La licencia existe pero quedo incompleta o con datos no confiables.",
    };
  }

  const expiration = new Date(licenciaVencimiento);
  expiration.setHours(23, 59, 59, 999);

  if (Number.isNaN(expiration.getTime()) || expiration.getTime() < Date.now()) {
    return {
      label: "Vencida",
      tone: "vencido",
      reason: "La licencia esta vencida o la fecha no es valida.",
    };
  }

  return {
    label: "Vigente",
    tone: "vigente",
    reason: "La licencia esta completa y vigente.",
  };
}

function buildDocumentStatus(documento) {
  if (!documento) {
    return {
      label: "Faltante",
      tone: "vencido",
      reason: "Documento no cargado.",
    };
  }

  const validationStatus = String(documento?.validacion?.status || "").trim().toLowerCase();
  if (validationStatus === "validated" || validationStatus === "approved") {
    return {
      label: "Vigente",
      tone: "vigente",
      reason: "Documento validado manualmente.",
    };
  }

  if (validationStatus === "rejected") {
    return {
      label: "Observado",
      tone: "faltante",
      reason: "Documento rechazado en validación.",
    };
  }

  if (validationStatus === "observed" || validationStatus === "pending_validation" || validationStatus === "pending_signature") {
    return {
      label: "Observado",
      tone: "faltante",
      reason: "Documento cargado con observaciones o pendiente de validación.",
    };
  }

  if (documento.estado !== "vigente") {
    return {
      label: "Observado",
      tone: "faltante",
      reason: "Documento cargado pero no marcado como vigente.",
    };
  }

  if (!documento.fecha_vencimiento) {
    return {
      label: "Revision",
      tone: "faltante",
      reason: "Falta fecha de vencimiento o dato confiable.",
    };
  }

  const expiration = new Date(documento.fecha_vencimiento);
  expiration.setHours(23, 59, 59, 999);

  if (Number.isNaN(expiration.getTime()) || expiration.getTime() < Date.now()) {
    return {
      label: "Vencido",
      tone: "vencido",
      reason: "Documento vencido o con fecha invalida.",
    };
  }

  return {
    label: "Vigente",
    tone: "vigente",
    reason: "Documento vigente.",
  };
}

function buildLicenciaDocumentCardStatus(chofer, documento) {
  const status = buildLicenciaStatus(chofer, documento);

  if (!documento) {
    return {
      label: "Sin foto",
      tone: "vencido",
      reason: "Todavia no hay foto cargada.",
    };
  }

  if (status.tone === "vigente") {
    return {
      label: "Aprobada",
      tone: "vigente",
      reason: "Licencia vigente y validada.",
    };
  }

  if (status.tone === "vencido") {
    return {
      label: "Vencida",
      tone: "vencido",
      reason: status.reason,
    };
  }

  return {
    label: "Observada",
    tone: "faltante",
    reason: "No se pudo vincular la licencia con los datos del chofer.",
  };
}

function buildDocumentCardStatus(documento) {
  if (!documento) {
    return {
      label: "Sin foto",
      tone: "vencido",
      reason: "Todavia no hay foto cargada.",
    };
  }

  const status = buildDocumentStatus(documento);

  if (status.tone === "vigente") {
    return {
      label: "Aprobada",
      tone: "vigente",
      reason: "Documento validado.",
    };
  }

  if (status.label === "Vencido") {
    return {
      label: "Vencida",
      tone: "vencido",
      reason: status.reason,
    };
  }

  return {
    label: "Observada",
    tone: "faltante",
    reason:
      documento?.estado && documento.estado !== "vigente"
        ? "Documento no legible o con datos observados."
        : "No se pudo vincular el documento con los datos del chofer.",
  };
}

function buildDniDocumentCardStatus(dniFrente, dniDorso) {
  if (!dniFrente && !dniDorso) {
    return {
      label: "Sin foto",
      tone: "vencido",
      reason: "Todavía no hay DNI cargado.",
      summary: "Sin foto cargada",
    };
  }

  if (!dniFrente || !dniDorso) {
    return {
      label: "Observada",
      tone: "faltante",
      reason: "Falta una de las caras del DNI.",
      summary: "Falta frente o dorso",
    };
  }

  return {
    label: "Aprobada",
    tone: "vigente",
    reason: "DNI frente y dorso cargados.",
    summary: `Alta ${formatDate([dniFrente.created_at, dniDorso.created_at].sort().reverse()[0])}`,
  };
}

function buildBiometriaDocumentCardStatus(biometria) {
  if (!biometria) {
    return {
      label: "Sin validar",
      tone: "vencido",
      reason: "Todavía no hay validación biométrica.",
      summary: "Sin validación registrada",
    };
  }

  return {
    label: biometria.validado ? "Aprobada" : "Observada",
    tone: biometria.validado ? "vigente" : "faltante",
    reason: biometria.validado ? "Biometría validada correctamente." : "La biometría quedó observada.",
    summary: biometria.created_at ? `Validada ${formatDate(biometria.created_at)}` : "Validación registrada",
  };
}


function getLatestDocumentsByType(documentos) {
  const latestByType = new Map();

  documentos.forEach((documento) => {
    const key = documento.tipo_documento || `doc-${documento.id}`;
    const current = latestByType.get(key);

    if (!current) {
      latestByType.set(key, documento);
      return;
    }

    const currentDate = new Date(current.created_at || 0).getTime();
    const nextDate = new Date(documento.created_at || 0).getTime();

    if (nextDate >= currentDate) {
      latestByType.set(key, documento);
    }
  });

  return Array.from(latestByType.values()).sort((left, right) => {
    const rightDate = new Date(right.created_at || 0).getTime();
    const leftDate = new Date(left.created_at || 0).getTime();
    return rightDate - leftDate;
  });
}

function mapAccessTone(status) {
  if (status === "access_granted" || status === "approved") {
    return "vigente";
  }

  if (status === "access_denied" || status === "revoked") {
    return "vencido";
  }

  return "faltante";
}

export function PortalChoferLinkedCompaniesScreen({ cuitFromRoute = "" }) {
  const [cuit] = useState(cuitFromRoute);
  const [links, setLinks] = useState([]);
  const [, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [respondingLinkId, setRespondingLinkId] = useState("");
  const [choferProfile, setChoferProfile] = useState(null);
  const [linkCuit, setLinkCuit] = useState("");
  const [companyPreview, setCompanyPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [autonomousError, setAutonomousError] = useState("");
  const [showAutonomousModal, setShowAutonomousModal] = useState(false);
  const [autonomousProcessing, setAutonomousProcessing] = useState(false);

  const normalizedTargetCuit = String(linkCuit || "").replace(/\D/g, "");
  const normalizedChoferCuit = String(cuit || "").replace(/\D/g, "");
  const linkedMessage = message;
  const showAutonomousFallback = normalizedChoferCuit.length === 11;

  function closeAutonomousModal() {
    if (autonomousProcessing) {
      return;
    }
    setShowAutonomousModal(false);
  }

  async function refreshChoferProfile() {
    if (!cuit) {
      setChoferProfile(null);
      return;
    }

    try {
      const response = await getJson(`/identidad/personas/${cuit}`);
      setChoferProfile(response.data || null);
    } catch (_error) {
      setChoferProfile(null);
    }
  }

  async function handleAutonomousSignup() {
    setAutonomousProcessing(true);
    setAutonomousError("");
    try {
      await postJson(`/portal-chofer/choferes/${normalizedChoferCuit}/autonomous-signup`, {
        nombre: choferProfile?.persona?.nombre || choferProfile?.chofer?.nombre || "",
        apellido: choferProfile?.persona?.apellido || choferProfile?.chofer?.apellido || "",
      });
      setShowAutonomousModal(false);
      setMessage("Empresa autónoma creada. El acceso del chofer queda resuelto con el WhatsApp validado.");
      await refreshLinks();
      await refreshChoferProfile();
    } catch (error) {
      setAutonomousError(error?.message || "No pudimos registrar al autónomo.");
    } finally {
      setAutonomousProcessing(false);
    }
  }

  const refreshLinks = useCallback(async () => {
    if (!cuit) {
      setLinks([]);
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const response = await getJson(`/portal-chofer/choferes/${cuit}/transport-links`);
      setLinks(response.data || []);
    } catch (error) {
      setLinks([]);
      const sanitized = error?.message?.includes("chofer_transport_links")
        ? "Los vínculos todavía no están habilitados."
        : error?.message || "No pudimos cargar los vínculos.";
      setMessage(sanitized);
    } finally {
      setLoading(false);
    }
  }, [cuit]);

  useEffect(() => {
    refreshLinks();
  }, [refreshLinks]);

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      if (!cuit) {
        setChoferProfile(null);
        return;
      }

      try {
        const response = await getJson(`/identidad/personas/${cuit}`);
        if (!cancelled) {
          setChoferProfile(response.data);
        }
      } catch (_error) {
        if (!cancelled) {
          setChoferProfile(null);
        }
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [cuit]);

  useEffect(() => {
    const normalized = String(linkCuit || "").replace(/\D/g, "");
    if (normalized.length !== 11) {
      setCompanyPreview(null);
      setPreviewError("");
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError("");
    getJson(`/companies/lookup?cuit=${normalized}`)
      .then((result) => {
        if (!cancelled) {
          setCompanyPreview(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setCompanyPreview(null);
          setPreviewError(error.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [linkCuit]);

  const driverPersonaName = `${choferProfile?.persona?.nombre || ""} ${choferProfile?.persona?.apellido || ""}`.trim();
  const heroLinkedName = driverPersonaName || "Chofer";
  const heroLinkedEmail = choferProfile?.chofer?.email || choferProfile?.persona?.email || "";
  const heroLinkedCuit = choferProfile?.cuit || cuit;
  const heroLinkedAside = (
    <div className="chofer-session-hero">
      <ChoferSessionSummary name={heroLinkedName} cuit={heroLinkedCuit} email={heroLinkedEmail} />
    </div>
  );
  const heroLinkedTitle = (
    <>
      <span>Empresas con</span>
      <span>las que trabajo</span>
    </>
  );
  const activeLinks = links.filter((link) => link?.status === "approved");
  const activeLinksCount = activeLinks.length;
  async function respondLink(linkId, status) {
    setRespondingLinkId(String(linkId));
    setMessage("");
    try {
      await patchJson(`/portal-chofer/transport-links/${linkId}`, { status });
      await refreshLinks();
      setMessage(status === "approved" ? "Vínculo aceptado." : "Vínculo actualizado.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRespondingLinkId("");
    }
  }

  async function requestTransportLink() {
    if (!cuit) {
      setMessage("Ingresa el CUIT del chofer antes de solicitar un vínculo.");
      return;
    }

    const targetCuit = normalizedTargetCuit;
    if (targetCuit.length !== 11) {
      setMessage("El CUIT de la empresa debe tener 11 dígitos válidos.");
      return;
    }

    setLinkSubmitting(true);
    setMessage("");
    try {
      await postJson(`/portal-transporte/choferes/${targetCuit}/transport-links`, {
        chofer_cuit: cuit,
        reason:
          companyPreview?.reasons?.join(" ") || undefined,
      });

      await refreshLinks();
      setMessage("Solicitud enviada. La empresa será notificada y podrá validarla cuando exista.");
      setLinkCuit("");
    } catch (error) {
      setMessage(error?.message || "No pudimos enviar la solicitud.");
    } finally {
      setLinkSubmitting(false);
    }
  }

  return (
    <PortalLayout
      {...choferHeroLayout}
      eyebrow="Portal Chofer"
      title={heroLinkedTitle}
      description="Agregá el CUIT de tus empresas de transporte o marcate como autónomo para crear tu propia empresa. Aquí podés ver los vínculos activos o pendientes mientras la otra parte los valida."
      backHref={getChoferAppHomeHash(cuit)}
      heroAside={heroLinkedAside}
    >
      <StatusMessage message={linkedMessage} />
      {showAutonomousFallback ? (
        <div className="autonomous-float">
              <button
                className="submit-button autonomous-float-button"
                type="button"
                disabled={normalizedChoferCuit.length !== 11}
                onClick={() => {
                  setAutonomousError("");
                  setShowAutonomousModal(true);
                }}
              >
            Registrar autónomo
          </button>
        </div>
      ) : null}
      {autonomousError ? <StatusMessage message={autonomousError} /> : null}
      {showAutonomousModal ? (
        <div className="autonomous-modal-backdrop" onClick={closeAutonomousModal}>
          <div className="autonomous-modal" onClick={(event) => event.stopPropagation()}>
            <div className="autonomous-modal-header">
              <h3>Registrar autónomo</h3>
              <button className="secondary-button" type="button" onClick={closeAutonomousModal}>
                Cerrar
              </button>
            </div>
            <p>
              Si facturás con el CUIT del chofer ({cuit}) generaremos automáticamente una empresa de transporte con ese nombre donde podés gestionar la información de tu perfil de autónomo.
            </p>
            <div className="autonomous-modal-login">
              <p className="autonomous-modal-note">
                Este alta ya no crea acceso por email. La empresa queda vinculada al chofer y el ingreso sigue resolviéndose con el WhatsApp validado del chofer.
              </p>
            </div>
            <div className="autonomous-modal-actions">
              <button
                className="submit-button"
                type="button"
                onClick={handleAutonomousSignup}
                disabled={autonomousProcessing}
              >
                {autonomousProcessing ? "Procesando..." : "Confirmar y crear empresa"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <>
        <section className="detail-panel detail-panel-wide">
            <div className="detail-panel-header">
              <h2>Relaciones</h2>
              <StatusBadge
                label={activeLinksCount ? `${activeLinksCount} vínculo${activeLinksCount === 1 ? "" : "s"}` : "Sin relaciones"}
                tone={activeLinksCount ? "vigente" : "pendiente"}
              />
            </div>
            <div className="panel-body">
              <div className="portal-list-card chofer-link-form">
                <div className="detail-panel-header">
                  <h3>Agregar vínculo</h3>
                  <p>Ingresá el CUIT de la empresa de transporte para mostrarte los datos oficiales antes de confirmar el vínculo.</p>
                </div>
                <div className="panel-body">
                  <div className="chofer-link-fields">
                    <Input label="CUIT de la empresa" value={linkCuit} onChange={(value) => setLinkCuit(value)} />
                    <div className="company-preview">
                      {previewLoading ? (
                        <p>Cargando datos oficiales...</p>
                      ) : companyPreview ? (
                        <>
                          <p>
                            <strong>Razón social:</strong> {companyPreview.company?.razon_social || "No disponible"}
                          </p>
                          <p>
                            <strong>Estado AFIP:</strong> {companyPreview.status || "-"}
                          </p>
                          {Array.isArray(companyPreview.reasons) && companyPreview.reasons.length ? (
                            <p>{companyPreview.reasons.join(" ")}</p>
                          ) : null}
                        </>
                      ) : previewError ? (
                        <p className="message">{previewError}</p>
                      ) : (
                        <p className="message">Escribí 11 dígitos de CUIT para ver la información oficial.</p>
                      )}
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      className="submit-button"
                      type="button"
                      onClick={requestTransportLink}
                      disabled={linkSubmitting || !cuit || normalizedTargetCuit.length !== 11}
                    >
                      {linkSubmitting ? "Enviando..." : "Solicitar vínculo"}
                    </button>
                  </div>
                </div>
              </div>
              {activeLinksCount ? (
                <div className="contract-list">
                  {activeLinks.map((link) => (
                    <details className="detail-panel expandable-card request-card" key={`chofer-transport-link-${link.id}`}>
                      <summary className="expandable-card-summary">
                        <div>
                          <h2>{link.transport_company_name || `Empresa ${link.transport_company_cuit}`}</h2>
                          <p>Estado activo</p>
                        </div>
                        <div className="expandable-card-summary-side">
                          <StatusBadge label="Activa" tone="vigente" />
                          <span className="expandable-card-chevron" aria-hidden="true">▾</span>
                        </div>
                      </summary>
                      <div className="expandable-card-body">
                        <div className="detail-table">
                          <div className="detail-row">
                            <span>CUIT empresa</span>
                            <strong>{link.transport_company_cuit}</strong>
                          </div>
                          <div className="detail-row">
                            <span>Solicitado por</span>
                            <strong>{humanizeLabel(link.requested_by_type)}</strong>
                          </div>
                          <div className="detail-row">
                            <span>Solicitado</span>
                            <strong>{formatDate(link.created_at)}</strong>
                          </div>
                        </div>
                        <div className="biometry-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            disabled={respondingLinkId === String(link.id)}
                            onClick={() => respondLink(link.id, "revoked")}
                          >
                            {respondingLinkId === String(link.id) ? "Procesando..." : "Desvincular"}
                          </button>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              ) : (
                <p className="message">Todavía no hay vínculos entre este chofer y ninguna empresa de transporte.</p>
              )}
            </div>
        </section>
      </>
    </PortalLayout>
  );
}

export function PortalChoferDebugScreen() {
  const [dniFrenteFile, setDniFrenteFile] = useState(null);
  const [dniDorsoFile, setDniDorsoFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [dniFrenteData, setDniFrenteData] = useState(null);
  const [dniDorsoData, setDniDorsoData] = useState(null);
  const [choferRecord, setChoferRecord] = useState(null);
  const [choferDetail, setChoferDetail] = useState(null);
  const [biometryResult, setBiometryResult] = useState(null);
  const [accessMethod, setAccessMethod] = useState("");
  const [flowMessage, setFlowMessage] = useState("");
  const [loadingKey, setLoadingKey] = useState("");
  const [biometryFailureCount, setBiometryFailureCount] = useState(0);

  const extractedData = mergeDniRegistroData(dniFrenteData, dniDorsoData);
  const choferCuit = choferRecord?.cuit || extractedData.cuit || "";
  const heroDebugName = choferRecord
    ? `${choferRecord.nombre || ""} ${choferRecord.apellido || ""}`.trim()
    : `${extractedData.nombre || ""} ${extractedData.apellido || ""}`.trim() || "Chofer";
  const heroDebugEmail = choferDetail?.chofer?.email || choferDetail?.persona?.email || "";
  const heroDebugAside = <ChoferSessionSummary name={heroDebugName} cuit={choferCuit} email={heroDebugEmail} />;
  const heroDebugTitle = (
    <>
      <span>Registro autónomo</span>
      <span>guiado</span>
    </>
  );

  async function scanDni(lado, file, setter) {
    if (!file) {
      setFlowMessage(`Captura el DNI ${lado} antes de procesarlo.`);
      return;
    }

    setLoadingKey(`dni-${lado}`);
    setFlowMessage(`Procesando OCR del DNI ${lado}...`);

    try {
      const response = await postForm("/identidad/registro-dni", { lado }, { archivo: file });
      setter(response.data);
      setFlowMessage(`OCR del DNI ${lado} completado.`);
    } catch (error) {
      setFlowMessage(error.message);
    } finally {
      setLoadingKey("");
    }
  }

  async function createOrFindChofer() {
    if (!extractedData.cuit || !extractedData.nombre || !extractedData.apellido) {
      setFlowMessage(
        "No se pudo resolver el CUIT con suficiente claridad. Sin CUIT no podemos crear o recuperar al chofer y la biometria no puede continuar."
      );
      return;
    }

    setLoadingKey("chofer-registro");
    setFlowMessage("Creando o actualizando chofer automaticamente...");

    try {
      const response = await postForm("/portal-chofer/choferes", {
        cuit: extractedData.cuit,
        nombre: extractedData.nombre,
        apellido: extractedData.apellido,
      }, {
        dni_frente: dniFrenteFile,
        dni_dorso: dniDorsoFile,
      });

      setChoferRecord(response.data);
      await refreshChoferDetail(response.data.cuit);
      setFlowMessage(
        dniFrenteData?.chofer_existente || dniDorsoData?.chofer_existente
          ? "Chofer encontrado y actualizado automaticamente."
          : "Chofer creado automaticamente a partir del DNI."
      );
    } catch (error) {
      setFlowMessage(error.message);
    } finally {
      setLoadingKey("");
    }
  }

  async function validateBiometry() {
    if (!choferCuit) {
      setFlowMessage("La biometria no puede ejecutarse porque todavia no hay un chofer identificado. Primero hay que resolver el CUIT desde el DNI.");
      return;
    }

    if (!dniFrenteFile || !selfieFile) {
      setFlowMessage("Captura el frente del DNI y la selfie antes de validar biometria.");
      return;
    }

    setLoadingKey("biometria");
    setFlowMessage("Subiendo capturas y validando biometria...");
    setBiometryResult(null);

    try {
      const response = await postForm("/portal-chofer/biometria", { chofer_cuit: choferCuit }, {
        dni_frente: dniFrenteFile,
        selfie: selfieFile,
      });
      setBiometryResult(response.data);
      await refreshChoferDetail(choferCuit);
      if (response.data.validado) {
        setFlowMessage(
          `Cuenta creada. Biometria validada con ${Number(response.data.similaridad).toFixed(2)}% de similitud.`
        );
        setBiometryFailureCount(0);
      } else {
        const nextFailures = biometryFailureCount + 1;
        setBiometryFailureCount(nextFailures);
        const failureSummary = `Biometria rechazada con ${Number(response.data.similaridad).toFixed(2)}% de similitud.`;
        setFlowMessage(`${failureSummary} Intento ${nextFailures}/3. Volvé a tomar la selfie o reiniciá el DNI.`);
      }
    } catch (error) {
      setFlowMessage(buildBiometryErrorMessage(error.message, choferCuit));
    } finally {
      setLoadingKey("");
    }
  }

  async function refreshChoferDetail(cuit) {
    if (!cuit) {
      return;
    }

    try {
      const response = await getJson(`/choferes/${cuit}`);
      setChoferDetail(response.data);
    } catch (_error) {
      setChoferDetail(null);
    }
  }

  return (
    <PortalLayout
      {...choferHeroLayout}
      eyebrow="Portal Chofer"
      title={heroDebugTitle}
      description="El alta se arma escaneando el DNI, extrayendo datos por OCR y validando biometria sin ingreso manual."
      backHref={getChoferAppHomeHash(choferDetail?.cuit)}
      heroAside={heroDebugAside}
    >
      <section className="detail-panel detail-panel-wide">
        <div className="detail-panel-header">
          <h2>Flujo de alta</h2>
        </div>
        <div className="portal-list flow-steps-list">
          {[
            "1. Escanear DNI frente",
            "2. Escanear DNI dorso",
            "3. OCR y extraccion de identidad",
            "4. Crear o recuperar chofer automaticamente",
            "5. Tomar selfie",
            "6. Validar biometria",
            "7. Elegir metodo de acceso futuro",
          ].map((item) => (
            <PortalListItem key={item} title={item} description="" />
          ))}
        </div>
        <StatusMessage message={flowMessage} />
      </section>

      <section className="portal-section-grid">
        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>1. Escanear DNI frente</h2>
          </div>
          <div className="panel-body">
            <CameraCapture mode="documento" title="DNI frente" description="Captura el frente del DNI para extraer identidad y datos personales." onConfirm={setDniFrenteFile} />
            <CaptureResultCard label="DNI frente" file={dniFrenteFile} emptyMessage="Todavia no hay captura del frente del DNI." />
            <button className="secondary-button" type="button" onClick={() => scanDni("frente", dniFrenteFile, setDniFrenteData)} disabled={loadingKey === "dni-frente"}>
              {loadingKey === "dni-frente" ? "Procesando..." : "Procesar DNI frente"}
            </button>
            {dniFrenteData ? <OcrResultCard title="OCR DNI frente" data={dniFrenteData} /> : null}
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>2. Escanear DNI dorso</h2>
          </div>
          <div className="panel-body">
            <CameraCapture mode="documento" title="DNI dorso" description="Captura el dorso del DNI para completar datos faltantes y reforzar deteccion." onConfirm={setDniDorsoFile} />
            <CaptureResultCard label="DNI dorso" file={dniDorsoFile} emptyMessage="Todavia no hay captura del dorso del DNI." />
            <button className="secondary-button" type="button" onClick={() => scanDni("dorso", dniDorsoFile, setDniDorsoData)} disabled={loadingKey === "dni-dorso"}>
              {loadingKey === "dni-dorso" ? "Procesando..." : "Procesar DNI dorso"}
            </button>
            {dniDorsoData ? <OcrResultCard title="OCR DNI dorso" data={dniDorsoData} /> : null}
          </div>
        </section>

        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>3. OCR y extraccion de identidad</h2>
          </div>
          <div className="panel-body">
            <ExtractedDriverCard data={extractedData} />
          </div>
        </section>

        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>4. Crear o recuperar chofer automaticamente</h2>
            {choferRecord ? <StatusBadge label="Chofer listo" tone="vigente" /> : null}
          </div>
          <div className="panel-body">
            <button className="submit-button" type="button" onClick={createOrFindChofer} disabled={loadingKey === "chofer-registro"}>
              {loadingKey === "chofer-registro" ? "Guardando..." : "Crear o buscar chofer"}
            </button>
            {choferRecord ? <InfoCard title="Chofer identificado" rows={buildRows(choferRecord, ["cuit", "nombre", "apellido"])} /> : null}
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>5. Tomar selfie</h2>
          </div>
          <div className="panel-body">
            <CameraCapture mode="selfie" title="Selfie" description="Captura una selfie centrada y bien iluminada para comparar contra el DNI." onConfirm={setSelfieFile} />
            <CaptureResultCard label="Selfie" file={selfieFile} emptyMessage="Todavia no hay selfie confirmada." />
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>6. Validar biometria</h2>
            {biometryResult ? <StatusBadge label={biometryResult.validado ? "Validado" : "Rechazado"} tone={biometryResult.validado ? "vigente" : "vencido"} /> : null}
          </div>
          <div className="panel-body">
            <button className="submit-button" type="button" onClick={validateBiometry} disabled={loadingKey === "biometria"}>
              {loadingKey === "biometria" ? "Validando..." : "Validar biometria"}
            </button>
            {biometryResult ? <BiometryResultCard result={biometryResult} /> : null}
            {choferDetail?.biometria ? <LastBiometryCard biometria={choferDetail.biometria} /> : null}
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>7. Elegir metodo de acceso futuro</h2>
            {accessMethod ? <StatusBadge label="Elegido" tone="vigente" /> : null}
          </div>
          <div className="panel-body">
            <p>Este paso define como el chofer va a ingresar mas adelante. No bloquea el alta y queda listo para integrarse al modulo de acceso.</p>
            <div className="access-method-grid">
              {[
                { value: "biometria", title: "Ingreso con biometria", description: "Prioriza selfie en vivo como metodo principal de acceso futuro." },
                { value: "link-magico", title: "Link magico", description: "Ingreso por enlace temporal enviado por un canal seguro." },
                { value: "credenciales", title: "Credenciales clasicas", description: "Preparado para email o telefono mas password en una etapa posterior." },
              ].map((method) => (
                <button
                  key={method.value}
                  className={`portal-list-card access-method-card ${accessMethod === method.value ? "access-method-card-active" : ""}`}
                  type="button"
                  onClick={() => {
                    setAccessMethod(method.value);
                    setFlowMessage(`Metodo de acceso futuro seleccionado: ${method.title}.`);
                  }}
                >
                  <strong>{method.title}</strong>
                  <p>{method.description}</p>
                </button>
              ))}
            </div>
            {accessMethod ? <p className="status-message">Metodo seleccionado: {humanizeLabel(accessMethod)}</p> : null}
          </div>
        </section>
      </section>
    </PortalLayout>
  );
}
